import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import GoogleMap from "./components/GoogleMap";
import { searchRealBusinesses, RealBusiness, lookupBusinessByName, lookupCategoryWithinRadius, getPlaceDetails } from "./services/businessAPI";
import aiService, { ProspectData, AIProspectAnalysis } from "./services/aiService";
import Login, { User } from "./components/Login";
import Logo from "./components/Logo";
import InstallPrompt from "./components/InstallPrompt";
import CallSheetView from "./components/CallSheetView";
import AppointmentCalendarView from "./components/AppointmentCalendarView";
import { onAuthStateChange, signOutUser, getUserProfile, saveCallLog, updateUserPreferences, deleteUser as deleteFirebaseUser, updateUserRole as updateFirebaseUserRole, clearUniversalAICache, getUniversalBlocklist, addCurrentAdvertiser, removeCurrentAdvertiser, generateInvitation, getInvitations, subscribeUniversalBlocklist, saveUniversalBlocklist, subscribeCurrentAdvertisers, getUserCallLogs, saveProspect, saveProspects, upsertProspect, getOrCreateTodayCallSheet, listCallSheets, listCallSheetItems, addProspectToCallSheet, removeSavedProspectByUserAndBusiness, clearUserCallLogs, getAllUsers, adminUpdateAuthUser, reauthenticateCurrentUser, getDueProspectReminders, getUserSavedProspectsV2, createCallSheet, deleteUserSearch, subscribeUserSearches } from "./services/firebase";
import type { AppointmentType, CategoryGroup, ProspectRecord } from "./types/prospecting";
import { CATEGORY_GROUPS, CATEGORY_GROUP_LABELS, formatMdy, formatMdyTime, formatDateKey } from "./types/prospecting";
import type { GemmetricLiteScanResult, OrganizationKey } from "./types/organizations";
import {
  DEFAULT_ORGANIZATION,
  ORGANIZATION_ANALYSIS_TITLES,
  ORGANIZATION_LABELS,
  ORGANIZATION_REASONING_LABELS,
  ORGANIZATION_RECOMMENDATIONS_LABELS,
  ORGANIZATION_SCORE_LABELS,
  isValidOrganizationKey,
  organizationStorageKey,
  operatingModeForOrganization,
  orgProspectDocId,
  recordMatchesOrg,
  resolveOrgKey,
  tagWithOrg
} from "./types/organizations";
import { readOrgScopedJson, writeOrgScopedJson, STORAGE_KEYS, migrateRecordOrgKey } from "./utils/orgScope";
import { enrichLiteScanForDisplay, formatFindingConfidenceLabel, formatOpportunityLevelLabel } from "./utils/gemmetricLiteScanReport";
import { runGemmetricLiteScan } from "./services/gemmetricLiteScan";

// Add CSS animation for header gradient and mobile responsiveness
const headerGradientStyle = document.createElement('style');
headerGradientStyle.innerHTML = `
  @keyframes headerGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
  
  @keyframes scanProgress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  
  @media (max-width: 768px) {
    .header-content {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 16px !important;
    }
    
    .brand-section {
      justify-content: center !important;
      text-align: center !important;
    }
    
    .user-info {
      justify-content: center !important;
      flex-direction: column !important;
      gap: 12px !important;
    }
    
    .user-details {
      align-items: center !important;
      text-align: center !important;
    }
    
    .user-info button {
      margin: 4px !important;
      min-width: 120px !important;
    }
  }
  
  @media (max-width: 480px) {
    .brand-section {
      flex-direction: column !important;
      gap: 12px !important;
    }
    
    .brand-subtitle {
      font-size: 14px !important;
    }
  }
`;
document.head.appendChild(headerGradientStyle);

interface Prospect {
  id: string;
  name: string;
  category: string;
  categoryGroup?: CategoryGroup;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  lat: number;
  lng: number;
  openNow: boolean;
  reviews: number;
  score?: number;
  rating?: number;
  placeId?: string;
  aiAnalysis?: AIProspectAnalysis | null;
  gemmetricLiteScan?: GemmetricLiteScanResult | null;
  orgKey?: OrganizationKey;
}

interface CallLogEntry {
  id: string;
  orgKey?: OrganizationKey;
  name: string;
  category: string;
  phone: string;
  website: string;
  address: string;
  timestamp: string;
  outcome: string;
  note: string;
  appointmentAt?: string;
  durationMinutes?: number;
  appointmentType?: AppointmentType;
  appointmentLocation?: string;
  appointmentContactName?: string;
  appointmentContactPhone?: string;
  appointmentContactEmail?: string;
}

interface DueReminderEntry {
  id: string;
  name: string;
  nextReminderAt: string;
  phone?: string;
  website?: string;
}

type OutcomeType = "Called" | "Visited" | "Saved for Later";

interface ScanHistoryItem {
  id: string;
  orgKey?: OrganizationKey;
  createdAt: string;
  locationLabel: string;
  radius: number;
  center?: { lat: number; lng: number };
  categories: string[];
  prospectCount: number;
  prospects: Prospect[];
}

// Scanning Animation Component
interface ScanningAnimationProps {
  isScanning: boolean;
}

const ScanningAnimation: React.FC<ScanningAnimationProps> = ({ isScanning }) => {
  const [pulseCount, setPulseCount] = useState(0);
  const [scanText, setScanText] = useState('Scanning for prospects');

  useEffect(() => {
    if (!isScanning) {
      setPulseCount(0);
      setScanText('Scanning for prospects');
      return;
    }

    // Pulse animation
    const pulseInterval = setInterval(() => {
      setPulseCount(prev => prev + 1);
    }, 800);

    // Text animation
    const textInterval = setInterval(() => {
      setScanText(prev => {
        const dots = '.'.repeat((pulseCount % 4) + 1);
        return `Scanning for prospects${dots}`;
      });
    }, 400);

    return () => {
      clearInterval(pulseInterval);
      clearInterval(textInterval);
    };
  }, [isScanning, pulseCount]);

  if (!isScanning) return null;

  return (
    <div
      style={{
        position: 'fixed', // Changed from 'absolute' to 'fixed'
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '20px 30px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minWidth: '280px',
        maxWidth: '90vw' // Ensure it doesn't overflow on mobile
      }}
    >
      {/* Scanning Icon */}
      <div
        style={{
          fontSize: '48px',
          marginBottom: '16px',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}
      >
        🔍
      </div>
      
      {/* Scanning Text */}
      <div
        style={{
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '8px',
          color: '#ffffff'
        }}
      >
        {scanText}
      </div>
      
      {/* Progress Bar */}
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '2px',
          overflow: 'hidden',
          marginTop: '12px'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#00ff88',
            borderRadius: '2px',
            animation: 'scanProgress 2s ease-in-out infinite',
            transformOrigin: 'left'
          }}
        />
      </div>
      
      {/* Status Text */}
      <div
        style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.7)',
          marginTop: '8px'
        }}
      >
        Analyzing prospects with AI...
      </div>
      
      {/* New: Time expectation text */}
      <div
        style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginTop: '12px',
          fontStyle: 'italic'
        }}
      >
        This process may take a few minutes to complete
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const SCAN_HISTORY_STORE_LIMIT = 180;
  const SCAN_HISTORY_UI_LIMIT = 10;
  const MAX_PRIMARY_SCAN_CATEGORIES = 5;
  const MAX_VERTICAL_SCAN_CANDIDATES = 125;
  const GEMMETRIC_AGENCY_RADIUS_OPTIONS = [5, 10, 15, 25, 30] as const;
  // User state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Location state
  const [userPos, setUserPos] = useState({ lat: 33.5186, lng: -86.8104 });
  const [currentLocation, setCurrentLocation] = useState<string>('Birmingham, AL');
  const [radius, setRadius] = useState(() => {
    // New desired range is 0.5 - 1.5 miles; default to 1.0
    return 1.0;
  });
  const [verticalRadius, setVerticalRadius] = useState(10);
  const [prospectCount, setProspectCount] = useState(20);
  const [addressInput, setAddressInput] = useState('');
  const [quickSearch, setQuickSearch] = useState('');
  const [quickMode, setQuickMode] = useState<'business' | 'category'>('business');
  const [scanMode, setScanMode] = useState<'local' | 'vertical'>('local');
  const [selectedOrg, setSelectedOrg] = useState<OrganizationKey>(DEFAULT_ORGANIZATION);
  const [orgPreferenceLoaded, setOrgPreferenceLoaded] = useState(false);
  const [liteScanLoadingById, setLiteScanLoadingById] = useState<Record<string, boolean>>({});
  const [selectedCategories, setSelectedCategories] = useState<CategoryGroup[]>([]);
  const [verticalCategory, setVerticalCategory] = useState<CategoryGroup>(CATEGORY_GROUPS[0]);
  
  // App state
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState<Prospect[]>([]);
  const [allProspects, setAllProspects] = useState<Prospect[]>([]);
  const [activeTab, setActiveTab] = useState<'prospects' | 'saved' | 'log' | 'activity' | 'call-sheet' | 'calendar' | 'admin' | 'all-prospects' | 'blocked' | 'current-advertisers'>('prospects');
  const [activityView, setActivityView] = useState<'call-sheet' | 'calendar'>('call-sheet');
  const [dueReminders, setDueReminders] = useState<DueReminderEntry[]>([]);
  const [isLoadingDueReminders, setIsLoadingDueReminders] = useState(false);
  const [clearingReminderId, setClearingReminderId] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(() => {
    // Initialize from localStorage first
    const hideInstructions = localStorage.getItem('raedar_hide_instructions');
    return hideInstructions !== 'true';
  });
  const [dontShowAgain, setDontShowAgain] = useState(() => {
    // Initialize from localStorage
    const hideInstructions = localStorage.getItem('raedar_hide_instructions');
    return hideInstructions === 'true';
  });
  
  // Admin state
  const [blocklist, setBlocklist] = useState<string[]>([]);
  const [blocklistLoaded, setBlocklistLoaded] = useState(false);
  const [cacheStats, setCacheStats] = useState<{ total: number; valid: number; expired: number }>({ total: 0, valid: 0, expired: 0 });
  
  // Debug: Log blocklist changes
  useEffect(() => {
    console.log('Blocklist state changed:', blocklist);
    if (blocklist.length === 0 && blocklistLoaded) {
      console.warn('WARNING: Blocklist was reset to empty after being loaded!');
      console.trace('Stack trace for blocklist reset');
    }
  }, [blocklist, blocklistLoaded]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [showAllProspects, setShowAllProspects] = useState(false);
  
  // Prospect management
  const [savedProspects, setSavedProspects] = useState<Prospect[]>([]);
  const [savedProspectMetaById, setSavedProspectMetaById] = useState<Record<string, ProspectRecord>>({});
  const [savedViewFilter, setSavedViewFilter] = useState<'all' | 'callbacks'>('all');
  const [savedCategoryFilter, setSavedCategoryFilter] = useState<'all' | CategoryGroup>('all');
  const [selectedSavedIds, setSelectedSavedIds] = useState<Record<string, boolean>>({});
  const [isAddingToCallSheet, setIsAddingToCallSheet] = useState(false);
  const [addingToWeekByProspectId, setAddingToWeekByProspectId] = useState<Record<string, boolean>>({});
  const [currentWeekSheetProspectIds, setCurrentWeekSheetProspectIds] = useState<Record<string, boolean>>({});
  const [isBuildingCallbackSheet, setIsBuildingCallbackSheet] = useState(false);
  const [log, setLog] = useState<CallLogEntry[]>([]);
  
  // Appointment strategy state
  const [appointmentStrategy, setAppointmentStrategy] = useState<{
    openingLine: string;
    bestTimeToCall: string;
    keyTalkingPoints: string[];
    objectionHandlers: string[];
    valueProposition: string;
    callToAction: string;
    competitiveAngles?: string[];
    verticalInsights?: string[];
    discoveryQuestions?: string[];
    hookLines?: string[];
    mediaMix?: Array<{ channel: string; percent?: number; why: string }> | string[];
    visibilityPriorities?: string[];
    liteScanInsights?: string[];
    plan30Day?: string[];
    introEmail?: string;
    followUpEmail?: string;
    voicemailScript?: string;
  } | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  
  // Pagination state for prospects
  const [prospectsLoaded, setProspectsLoaded] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreProspects, setHasMoreProspects] = useState(false);
  const [allAvailableProspects, setAllAvailableProspects] = useState<Prospect[]>([]);

  // Current advertiser state
  const [currentAdvertisers, setCurrentAdvertisers] = useState<Record<string, { businessName: string; markedByName: string; markedAt: Date }>>({});
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
  const [showScanCoverage, setShowScanCoverage] = useState(true);
  const [scanCoverageDays, setScanCoverageDays] = useState<30 | 90 | 180>(90);
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  const lastLoadedUserIdRef = useRef<string | null>(null);
  const lastOrgLoadedUserRef = useRef<string | null>(null);
  const previousOrgRef = useRef<OrganizationKey>(DEFAULT_ORGANIZATION);
  const selectedOrgRef = useRef<OrganizationKey>(DEFAULT_ORGANIZATION);
  const activeScanIdRef = useRef<string | null>(null);
  const scanHistoryRef = useRef<ScanHistoryItem[]>([]);
  const scanResultsRef = useRef<Prospect[]>([]);
  const scanHistoryHydratedRef = useRef(false);
  const orgWorkspaceHydratingRef = useRef(false);
  const promotedScanSignaturesRef = useRef<Record<string, boolean>>({});

  const prospectsBelongToOrg = (prospects: Prospect[], orgKey: OrganizationKey): boolean => {
    if (!prospects.length) return true;
    return prospects.every((prospect) => recordMatchesOrg(prospect, orgKey));
  };

  const filterProspectsForOrg = (prospects: Prospect[], orgKey: OrganizationKey): Prospect[] =>
    (prospects || [])
      .filter((prospect) => recordMatchesOrg(prospect, orgKey))
      .map((prospect) => tagProspectForOrg(prospect, orgKey));

  const canPersistOrgWorkspace = (
    orgKey: OrganizationKey,
    prospects: Prospect[] = [],
    history: ScanHistoryItem[] = scanHistory
  ): boolean => {
    if (orgWorkspaceHydratingRef.current) return false;
    if (!prospectsBelongToOrg(prospects, orgKey)) return false;
    if (history.length > 0 && history.some((item) => !recordMatchesOrg(item, orgKey))) return false;
    return true;
  };

  // Soft-deprecate legacy Log tab by rerouting stale state into Activity.
  useEffect(() => {
    if (activeTab === 'log') setActiveTab('activity');
  }, [activeTab]);

  // Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userProfile = await getUserProfile(firebaseUser.uid);
          if (userProfile) {
            const createdAtIso = (userProfile?.createdAt && typeof (userProfile.createdAt as any).toDate === 'function')
              ? (userProfile.createdAt as any).toDate().toISOString()
              : (typeof userProfile.createdAt === 'string' ? userProfile.createdAt : new Date().toISOString());
            const user: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: userProfile.name,
              role: userProfile.role,
              createdAt: createdAtIso,
              isSuperAdmin: userProfile.isSuperAdmin
            };
            setCurrentUser(user);
            
            // Load user's instruction preference from Firebase
            console.log('Loading hideInstructions preference:', userProfile.hideInstructions);
            if (userProfile.hideInstructions) {
              console.log('Setting showInstructions to false');
              setShowInstructions(false);
              setDontShowAgain(true);
              console.log('✅ Instructions preference loaded: HIDE');
            } else {
              console.log('hideInstructions is false, showing instructions');
              setShowInstructions(true);
              setDontShowAgain(false);
              console.log('✅ Instructions preference loaded: SHOW');
            }
            
            console.log('🔄 Firebase user session restored:', user.name);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        // Clear any leftover data when no user is logged in
        console.log('🧹 No Firebase user session found, clearing any leftover data...');
        setCurrentUser(null);
        setScanResults([]);
        setAllProspects([]);
        setSavedProspects([]);
        setSavedProspectMetaById({});
        setLog([]);
      }
    });

    return () => unsubscribe();
  }, []);
  
  // Clear all user-specific data when switching users
  const clearUserData = (userIdToClear?: string) => {
    console.log('🧹 Clearing user-specific data...');
    
    // Clear search results and app state
    setScanResults([]);
    setAllProspects([]);
    setActiveTab('prospects');
    // Don't reset showInstructions here - let Firebase preference control it
    
    // Clear pagination state
    setProspectsLoaded(0);
    setHasMoreProspects(false);
    setAllAvailableProspects([]);
    
    // Clear appointment strategy state
    setAppointmentStrategy(null);
    setShowAppointmentModal(false);
    setIsGeneratingStrategy(false);
    
    // Clear user-specific localStorage data for the specified user (or current user if none specified)
    const userId = userIdToClear || currentUser?.id;
    if (userId) {
      localStorage.removeItem(`raedar_call_logs_${userId}`);
      localStorage.removeItem(`raedar_saved_prospects_${userId}`);
      localStorage.removeItem(`raedar_prospect_states_${userId}`);
      localStorage.removeItem(`raedar_search_results_${userId}`);
      localStorage.removeItem(`raedar_location_${userId}`);
      localStorage.removeItem(`raedar_search_params_${userId}`);
      localStorage.removeItem(`raedar_scan_results_${userId}`);
      localStorage.removeItem(`raedar_all_prospects_${userId}`);
    }
    
    console.log('✅ User data cleared successfully');
  };

  const tagProspectForOrg = (prospect: Prospect, orgKey: OrganizationKey = selectedOrg): Prospect => {
    return tagWithOrg(
      {
        ...prospect,
        gemmetricLiteScan: orgKey === 'gemmetric' ? prospect.gemmetricLiteScan : undefined
      },
      orgKey
    );
  };

  const restoreCachedScanResults = (userId: string, orgKey: OrganizationKey = selectedOrg): boolean => {
    try {
      const savedScan = readOrgScopedJson<Prospect[]>(STORAGE_KEYS.scanResults, userId, orgKey) || [];
      const savedAll = readOrgScopedJson<Prospect[]>(STORAGE_KEYS.allProspects, userId, orgKey) || [];
      const hasScan = Array.isArray(savedScan) && savedScan.length > 0;
      const hasAll = Array.isArray(savedAll) && savedAll.length > 0;
      if (!hasScan && !hasAll) return false;
      const all = filterProspectsForOrg(hasAll ? savedAll : savedScan, orgKey);
      const visible = filterProspectsForOrg(hasScan ? savedScan : all, orgKey);
      if (!visible.length && !all.length) return false;
      setScanResults(visible);
      setAllProspects(all);
      setAllAvailableProspects(all);
      setProspectsLoaded(visible.length);
      setHasMoreProspects(visible.length < Math.min(100, all.length));
      writeOrgScopedJson(STORAGE_KEYS.scanResults, userId, orgKey, visible);
      writeOrgScopedJson(STORAGE_KEYS.allProspects, userId, orgKey, all);
      console.log(`🔄 Restored cached scan for user ${userId} (${orgKey}): ${visible.length}/${all.length} prospects`);
      return true;
    } catch (error) {
      console.warn("Failed restoring cached scan results:", error);
      return false;
    }
  };

  const parseCreatedAtIso = (value: any): string => {
    if (value && typeof value.toDate === "function") return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    }
    return new Date().toISOString();
  };

  const normalizeScanHistoryEntries = (entries: ScanHistoryItem[]): ScanHistoryItem[] => {
    const deduped = new Map<string, ScanHistoryItem>();
    (entries || []).forEach((item) => {
      if (!item || !Array.isArray(item.prospects) || item.prospects.length === 0) return;
      const createdAt = parseCreatedAtIso(item.createdAt);
      const center =
        item.center && Number.isFinite(item.center.lat) && Number.isFinite(item.center.lng)
          ? item.center
          : undefined;
      deduped.set(String(item.id || `scan_${createdAt}`), {
        ...item,
        id: String(item.id || `scan_${createdAt}`),
        createdAt,
        radius: Number.isFinite(item.radius) ? item.radius : 0,
        center,
        categories: Array.isArray(item.categories) ? item.categories.filter((c) => typeof c === 'string') : [],
        prospectCount: Number.isFinite(item.prospectCount) ? item.prospectCount : item.prospects.length
      });
    });
    return Array.from(deduped.values())
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, SCAN_HISTORY_STORE_LIMIT);
  };

  const pickPreferredScan = (history: ScanHistoryItem[], preferredIds: Array<string | null | undefined>): ScanHistoryItem | null => {
    for (const candidate of preferredIds) {
      if (!candidate) continue;
      const match = history.find((item) => item.id === candidate);
      if (match) return match;
    }
    return history[0] || null;
  };

  const isLocalOnlyScanId = (scanId?: string | null) => String(scanId || '').startsWith('local_');

  const mergeRemoteAndLocalScanHistory = (
    remoteHistory: ScanHistoryItem[],
    localHistory: ScanHistoryItem[]
  ): ScanHistoryItem[] => {
    const localOnly = (localHistory || []).filter((item) => isLocalOnlyScanId(item.id));
    return normalizeScanHistoryEntries([...(remoteHistory || []), ...localOnly]);
  };

  const clearActiveScanResults = () => {
    setActiveScanId(null);
    setScanResults([]);
    setAllProspects([]);
    setAllAvailableProspects([]);
    setProspectsLoaded(0);
    setHasMoreProspects(false);
  };

  const getScanHistorySignature = (scan: ScanHistoryItem): string => {
    const categories = Array.isArray(scan.categories) ? [...scan.categories].sort().join(',') : '';
    return [
      parseCreatedAtIso(scan.createdAt),
      scan.locationLabel || '',
      Number.isFinite(scan.radius) ? String(scan.radius) : '0',
      categories,
      String(scan.prospectCount || scan.prospects.length || 0)
    ].join('|');
  };

  const formatScanCategories = (categories: string[] | undefined): string => {
    if (!Array.isArray(categories) || categories.length === 0) return 'All industries';
    return categories
      .map((category) => CATEGORY_GROUP_LABELS[category as CategoryGroup] || category.replace(/_/g, ' '))
      .join(', ');
  };

  const normalizeProspectCategoryData = (prospect: Prospect): Prospect => {
    const inferredGroup = inferCategoryGroupFromText(prospect.category);
    const normalizedGroup = inferredGroup || prospect.categoryGroup;
    return normalizedGroup && prospect.categoryGroup !== normalizedGroup
      ? { ...prospect, categoryGroup: normalizedGroup }
      : prospect;
  };

  const getProspectMergeKey = (prospect: Partial<Prospect>): string => {
    const candidatePlaceId = (prospect as any).placeId || prospect.id;
    if (isLikelyGooglePlaceId(candidatePlaceId)) return String(candidatePlaceId);
    return aiService.generateBusinessHash({
      name: prospect.name || '',
      category: prospect.category || 'Business',
      address: prospect.address || '',
      distance: 0,
      types: [prospect.category || 'Business'],
      businessStatus: prospect.openNow ? 'Open' : 'Closed',
      website: prospect.website || '',
      phone: prospect.phone || ''
    });
  };

  const dedupeSavedProspects = (prospects: Prospect[]): Prospect[] => {
    const deduped = new Map<string, Prospect>();
    prospects.forEach((prospect) => {
      const normalized = normalizeProspectCategoryData(prospect);
      deduped.set(getProspectMergeKey(normalized), normalized);
    });
    return Array.from(deduped.values());
  };

  const mergeSavedProspectSources = (legacyRows: any[], v2Rows: ProspectRecord[]): Prospect[] => {
    const legacyMap = new Map<string, Prospect>();
    (legacyRows || []).forEach((row: any) => {
      const prospect = normalizeProspectCategoryData((row?.prospect || row) as Prospect);
      legacyMap.set(getProspectMergeKey(prospect), prospect);
    });

    const merged: Prospect[] = [];
    const seen = new Set<string>();

    (v2Rows || []).forEach((record) => {
      const key = String(record.id);
      const legacy = legacyMap.get(key);
      merged.push(normalizeProspectCategoryData(tagProspectForOrg({
        id: legacy?.id || record.placeId || record.id,
        name: legacy?.name || record.name,
        category: record.categoryLabel || legacy?.category || 'Business',
        categoryGroup: record.categoryGroup || legacy?.categoryGroup,
        address: record.address || legacy?.address || '',
        city: record.city || legacy?.city || '',
        state: record.state || legacy?.state || '',
        zip: record.zip || legacy?.zip || '',
        phone: record.phone || legacy?.phone || '',
        website: record.website || legacy?.website || '',
        lat: Number(record.lat ?? legacy?.lat ?? 0),
        lng: Number(record.lng ?? legacy?.lng ?? 0),
        rating: legacy?.rating,
        reviews: legacy?.reviews || 0,
        openNow: legacy?.openNow ?? true,
        placeId: record.placeId || legacy?.placeId,
        score: legacy?.score,
        aiAnalysis: legacy?.aiAnalysis || null,
        gemmetricLiteScan: record.gemmetricLiteScan || record.orgInsights?.gemmetricLiteScan || legacy?.gemmetricLiteScan || null
      }, resolveOrgKey(record.orgKey))));
      seen.add(key);
    });

    legacyMap.forEach((prospect, key) => {
      if (!seen.has(key) && recordMatchesOrg(prospect, selectedOrgRef.current)) {
        merged.push(tagProspectForOrg(prospect, selectedOrgRef.current));
      }
    });

    return dedupeSavedProspects(merged);
  };

  const inferCategoryGroupFromText = (value?: string): CategoryGroup | undefined => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return undefined;

    const aliasPatterns: Array<{ group: CategoryGroup; patterns: string[] }> = [
      { group: 'legal', patterns: ['legal', 'attorney', 'law office', 'law firm', 'personal injury'] },
      { group: 'dental', patterns: ['dental', 'dentist', 'orthodont'] },
      { group: 'medical', patterns: ['medical', 'doctor', 'clinic', 'hospital', 'physician', 'primary care'] },
      { group: 'vision', patterns: ['vision', 'optometrist', 'eye care'] },
      { group: 'hearing', patterns: ['hearing', 'audiology', 'audiologist'] },
      { group: 'insurance', patterns: ['insurance'] },
      { group: 'financial', patterns: ['financial', 'bank', 'credit union', 'accounting', 'accountant'] },
      { group: 'automotive', patterns: ['automotive', 'auto dealer', 'auto repair', 'car dealer', 'car wash'] },
      { group: 'furniture', patterns: ['furniture'] },
      { group: 'mattress', patterns: ['mattress'] },
      { group: 'home_services', patterns: ['home services', 'contractor', 'landscap', 'painting', 'cleaning service'] },
      { group: 'hvac', patterns: ['hvac', 'heating', 'air conditioning', 'climate control'] },
      { group: 'plumbing', patterns: ['plumbing', 'plumber'] },
      { group: 'electrical', patterns: ['electrical', 'electrician'] },
      { group: 'roofing', patterns: ['roofing', 'roofer'] },
      { group: 'flooring', patterns: ['flooring', 'tile'] },
      { group: 'beauty_wellness', patterns: ['beauty', 'salon', 'spa', 'wellness', 'massage'] },
      { group: 'fitness', patterns: ['fitness', 'gym'] },
      { group: 'education_childcare', patterns: ['education', 'school', 'college', 'university', 'childcare', 'daycare', 'preschool'] },
      { group: 'senior_living', patterns: ['senior living', 'assisted living', 'retirement', 'nursing home'] }
    ];

    for (const { group, patterns } of aliasPatterns) {
      if (patterns.some((pattern) => normalized.includes(pattern))) return group;
    }

    return CATEGORY_GROUPS.find((group) => {
      return group === normalized || CATEGORY_GROUP_LABELS[group].toLowerCase() === normalized;
    });
  };

  const projectedScanRadius = scanMode === 'vertical' ? verticalRadius : radius;

  const filterScanHistoryForOrg = (
    history: ScanHistoryItem[],
    orgKey: OrganizationKey
  ): ScanHistoryItem[] => history.filter((item) => recordMatchesOrg(item, orgKey));

  const applyScanPayload = (
    prospects: Prospect[],
    scanId?: string,
    orgKey: OrganizationKey = selectedOrgRef.current
  ) => {
    const safe = Array.isArray(prospects)
      ? prospects.map((prospect) => tagProspectForOrg(prospect, orgKey))
      : [];
    setScanResults(safe);
    setAllProspects(safe);
    setAllAvailableProspects(safe);
    setProspectsLoaded(safe.length);
    setHasMoreProspects(false);
    if (scanId) setActiveScanId(scanId);
  };

  const buildHistoryFromSearches = (searches: any[]): ScanHistoryItem[] => {
    return normalizeScanHistoryEntries((searches || [])
      .map((search: any) => {
        const prospects = Array.isArray(search?.prospects) ? (search.prospects as Prospect[]) : [];
        const searchParams = search?.searchParams || {};
        const locationLabel =
          searchParams?.locationName ||
          searchParams?.currentLocation ||
          (searchParams?.location?.lat && searchParams?.location?.lng
            ? `${Number(searchParams.location.lat).toFixed(4)}, ${Number(searchParams.location.lng).toFixed(4)}`
            : "Unknown area");
        const radiusVal = Number(searchParams?.radius || 0);
        const searchLat = Number(searchParams?.location?.lat);
        const searchLng = Number(searchParams?.location?.lng);
        const fallbackLat = Number(prospects?.[0]?.lat);
        const fallbackLng = Number(prospects?.[0]?.lng);
        const hasSearchCenter = Number.isFinite(searchLat) && Number.isFinite(searchLng);
        const hasFallbackCenter = Number.isFinite(fallbackLat) && Number.isFinite(fallbackLng);
        const scanOrg = resolveOrgKey(search?.orgKey || searchParams?.orgKey);
        return {
          id: String(search?.id || `scan_${Date.now()}`),
          orgKey: scanOrg,
          createdAt: parseCreatedAtIso(search?.createdAt || searchParams?.createdAt),
          locationLabel,
          radius: Number.isFinite(radiusVal) ? radiusVal : 0,
          center: hasSearchCenter
            ? { lat: searchLat, lng: searchLng }
            : (hasFallbackCenter ? { lat: fallbackLat, lng: fallbackLng } : undefined),
          categories: Array.isArray(searchParams?.categories) ? searchParams.categories : [],
          prospectCount: prospects.length,
          prospects: prospects.map((p) => tagProspectForOrg(p, scanOrg))
        } as ScanHistoryItem;
      })
      .filter((item) => item.prospectCount > 0));
  };

  const restoreScanHistoryFromLocal = (userId: string, orgKey: OrganizationKey = selectedOrg): ScanHistoryItem[] => {
    try {
      const parsed = readOrgScopedJson<ScanHistoryItem[]>(STORAGE_KEYS.scanHistory, userId, orgKey);
      if (!Array.isArray(parsed)) return [];
      return normalizeScanHistoryEntries(parsed
        .filter((item: any) => item && Array.isArray(item.prospects) && item.prospects.length > 0)
        .map((item) => ({
          ...migrateRecordOrgKey(item),
          prospects: (item.prospects || []).map((p: Prospect) => tagProspectForOrg(p, resolveOrgKey(item.orgKey || orgKey)))
        }))
      );
    } catch {
      return [];
    }
  };

  const purgeMismatchedOrgCache = (userId: string, orgKey: OrganizationKey) => {
    const savedScan = readOrgScopedJson<Prospect[]>(STORAGE_KEYS.scanResults, userId, orgKey) || [];
    const savedAll = readOrgScopedJson<Prospect[]>(STORAGE_KEYS.allProspects, userId, orgKey) || [];
    const filteredScan = filterProspectsForOrg(savedScan, orgKey);
    const filteredAll = filterProspectsForOrg(savedAll, orgKey);
    const filteredHistory = filterScanHistoryForOrg(restoreScanHistoryFromLocal(userId, orgKey), orgKey);
    if (filteredScan.length !== savedScan.length) {
      writeOrgScopedJson(STORAGE_KEYS.scanResults, userId, orgKey, filteredScan);
    }
    if (filteredAll.length !== savedAll.length) {
      writeOrgScopedJson(STORAGE_KEYS.allProspects, userId, orgKey, filteredAll);
    }
    if (filteredHistory.length !== restoreScanHistoryFromLocal(userId, orgKey).length) {
      writeOrgScopedJson(STORAGE_KEYS.scanHistory, userId, orgKey, filteredHistory);
    }
  };

  const loadOrgScanWorkspace = (userId: string, orgKey: OrganizationKey) => {
    orgWorkspaceHydratingRef.current = true;
    try {
      purgeMismatchedOrgCache(userId, orgKey);
      const history = filterScanHistoryForOrg(restoreScanHistoryFromLocal(userId, orgKey), orgKey);
      setScanHistory(history);
      const activeId = readOrgScopedJson<string | null>(STORAGE_KEYS.activeScanId, userId, orgKey);
      setActiveScanId(activeId || null);
      if (activeId) {
        const active = history.find((item) => item.id === activeId);
        if (active) {
          applyScanPayload(active.prospects, activeId, orgKey);
          return;
        }
      }
      if (history[0]) {
        applyScanPayload(history[0].prospects, history[0].id, orgKey);
        return;
      }
      if (!restoreCachedScanResults(userId, orgKey)) {
        clearActiveScanResults();
      }
    } finally {
      orgWorkspaceHydratingRef.current = false;
    }
  };

  const handleOrganizationChange = (orgKey: OrganizationKey) => {
    if (!currentUser || orgKey === selectedOrgRef.current) return;
    previousOrgRef.current = orgKey;
    selectedOrgRef.current = orgKey;
    scanHistoryHydratedRef.current = false;
    // Drop previous-org CRM state immediately so tabs don't keep showing shared lists.
    setSavedProspects([]);
    setSavedProspectMetaById({});
    setLog([]);
    setBlocklist([]);
    setBlocklistLoaded(false);
    setCurrentAdvertisers({});
    setSelectedOrg(orgKey);
    if (orgKey === 'gemmetric') {
      setScanMode('vertical');
      setVerticalCategory('agency_services');
      setVerticalRadius(25);
      setSelectedCategories(['agency_services']);
    } else {
      setSelectedCategories((current) => current.filter((group) => group !== 'agency_services'));
    }
    loadOrgScanWorkspace(currentUser.id, orgKey);
    void reloadOrgCrmData(currentUser.id, orgKey);
    setCurrentWeekSheetProspectIds({});
  };

  const reloadOrgCrmData = async (userId: string, orgKey: OrganizationKey) => {
    try {
      const [callLogs, v2Saved] = await Promise.all([
        getUserCallLogs(userId, orgKey),
        getUserSavedProspectsV2(userId, orgKey)
      ]);
      const normalizedLogs: CallLogEntry[] = (callLogs || []).map((entry: any) => ({
        ...entry,
        orgKey: resolveOrgKey(entry.orgKey),
        timestamp: entry.timestamp || entry.createdAt?.toDate?.()?.toISOString?.() || new Date().toISOString()
      }));
      setLog(normalizedLogs);
      const mergedSaved = mergeSavedProspectSources([], v2Saved);
      setSavedProspects(mergedSaved);
      writeOrgScopedJson(STORAGE_KEYS.callLogs, userId, orgKey, normalizedLogs);
      writeOrgScopedJson(STORAGE_KEYS.savedProspects, userId, orgKey, mergedSaved);
    } catch (error) {
      console.warn('Failed reloading org CRM data:', error);
    }
  };

  useEffect(() => {
    activeScanIdRef.current = activeScanId;
  }, [activeScanId]);

  useLayoutEffect(() => {
    scanHistoryRef.current = scanHistory;
  }, [scanHistory]);

  useLayoutEffect(() => {
    scanResultsRef.current = scanResults;
  }, [scanResults]);

  useEffect(() => {
    selectedOrgRef.current = selectedOrg;
  }, [selectedOrg]);

  useEffect(() => {
    if (selectedOrg !== 'gemmetric') return;
    setScanMode('vertical');
    setVerticalCategory('agency_services');
    setSelectedCategories(['agency_services']);
    setVerticalRadius((current) => current === 10 ? 25 : current);
  }, [selectedOrg]);

  const migrateLegacySavedProspectsToV2 = async (userId: string, legacySaved: Prospect[]) => {
    const flagKey = `raedar_migrated_saved_to_prospects_v2_${userId}`;
    if (localStorage.getItem(flagKey) === 'true') return;
    if (!Array.isArray(legacySaved) || legacySaved.length === 0) {
      localStorage.setItem(flagKey, 'true');
      return;
    }

    try {
      for (const p of legacySaved) {
        await ensureProspectV2(userId, p, true);
      }

      localStorage.setItem(flagKey, 'true');
      console.log(`✅ Migrated ${legacySaved.length} legacy saved prospects to prospects(v2)`);
    } catch (e) {
      console.warn('Legacy saved->prospects(v2) migration failed (will retry next session):', e);
    }
  };

  // Load user-specific data after user and org preference are ready
  useEffect(() => {
    if (!currentUser || !orgPreferenceLoaded) {
      if (!currentUser) {
        lastLoadedUserIdRef.current = null;
        scanHistoryHydratedRef.current = false;
        promotedScanSignaturesRef.current = {};
        setScanHistory([]);
        setActiveScanId(null);
        setSavedProspectMetaById({});
      }
      return;
    }

    console.log('Loading data for user:', currentUser.id);
    const isUserSwitch = lastLoadedUserIdRef.current !== currentUser.id;
    lastLoadedUserIdRef.current = currentUser.id;
    scanHistoryHydratedRef.current = false;
    promotedScanSignaturesRef.current = {};

    if (isUserSwitch) {
      // Only reset scan state when the actual user changes
      setScanResults([]);
      setAllProspects([]);
      setActiveTab('prospects');
      // Don't reset showInstructions here - let Firebase preference control it
      setProspectsLoaded(0);
      setHasMoreProspects(false);
      setAllAvailableProspects([]);
      setAppointmentStrategy(null);
      setShowAppointmentModal(false);
      setIsGeneratingStrategy(false);
    }

    // Restore local history/cache immediately, then refresh from Firebase
    const storedActiveScanId = readOrgScopedJson<string | null>(STORAGE_KEYS.activeScanId, currentUser.id, selectedOrg);
    const localHistory = filterScanHistoryForOrg(
      restoreScanHistoryFromLocal(currentUser.id, selectedOrg),
      selectedOrg
    );
    const localOnlyHistory = normalizeScanHistoryEntries(localHistory.filter((item) => isLocalOnlyScanId(item.id)));
    if (localOnlyHistory.length > 0) {
      const preferredLocal = pickPreferredScan(localOnlyHistory, [storedActiveScanId, activeScanId]);
      setScanHistory(localOnlyHistory);
      if (preferredLocal) applyScanPayload(preferredLocal.prospects, preferredLocal.id, selectedOrg);
    }
    const restoredFromLocal = localOnlyHistory.length > 0;
    const orgForSubscription = selectedOrg;
    const unsubscribeSearches = subscribeUserSearches(
      currentUser.id,
      (searches) => {
        if (selectedOrgRef.current !== orgForSubscription) return;

        const remoteHistory = filterScanHistoryForOrg(buildHistoryFromSearches(searches), orgForSubscription);
        const restoredLocalHistory = filterScanHistoryForOrg(
          restoreScanHistoryFromLocal(currentUser.id, orgForSubscription),
          orgForSubscription
        );
        const latestLocalOnly = normalizeScanHistoryEntries(
          restoredLocalHistory.filter((item) => isLocalOnlyScanId(item.id))
        );
        const inMemoryLocalOnly = normalizeScanHistoryEntries(
          filterScanHistoryForOrg(scanHistoryRef.current, orgForSubscription).filter((item) => isLocalOnlyScanId(item.id))
        );
        const mergedHistory = mergeRemoteAndLocalScanHistory(remoteHistory, [
          ...latestLocalOnly,
          ...inMemoryLocalOnly
        ]);
        const currentActive = activeScanIdRef.current;
        const currentActiveExists = currentActive
          ? mergedHistory.some((item) => item.id === currentActive)
          : false;

        const remoteSignatures = new Set(remoteHistory.map(getScanHistorySignature));
        restoredLocalHistory.forEach((scan) => {
          const signature = getScanHistorySignature(scan);
          const ageMs = Date.now() - new Date(parseCreatedAtIso(scan.createdAt)).getTime();
          const eligibleForPromotion =
            Array.isArray(scan.prospects) &&
            scan.prospects.length > 0 &&
            ageMs > 2 * 60 * 1000 &&
            !remoteSignatures.has(signature) &&
            !promotedScanSignaturesRef.current[signature];
          if (!eligibleForPromotion) return;
          promotedScanSignaturesRef.current[signature] = true;
          const promotionOrg = resolveOrgKey(scan.orgKey || orgForSubscription);
          void saveProspects(currentUser.id, scan.prospects, {
            operatingMode: operatingModeForOrganization(orgForSubscription),
            createdAt: parseCreatedAtIso(scan.createdAt),
            locationName: scan.locationLabel,
            radius: scan.radius,
            prospectCount: scan.prospectCount,
            categories: scan.categories,
            location: scan.center,
            orgKey: promotionOrg
          }, promotionOrg).then(() => {
            console.log(`☁️ Promoted legacy local scan to Firebase: ${scan.locationLabel}`);
          }).catch((error) => {
            console.warn('Failed promoting cached scan to Firebase:', error);
            delete promotedScanSignaturesRef.current[signature];
          });
        });

        setScanHistory(mergedHistory);

        if (mergedHistory.length === 0) {
          if (!scanHistoryHydratedRef.current && !restoredFromLocal) {
            if (!restoreCachedScanResults(currentUser.id, orgForSubscription)) {
              if (
                !scanResultsRef.current.length ||
                !prospectsBelongToOrg(scanResultsRef.current, orgForSubscription)
              ) {
                clearActiveScanResults();
              }
            }
          } else if (
            !currentActiveExists &&
            (!scanResultsRef.current.length || !prospectsBelongToOrg(scanResultsRef.current, orgForSubscription))
          ) {
            clearActiveScanResults();
          }
          scanHistoryHydratedRef.current = true;
          return;
        }

        if (!scanHistoryHydratedRef.current) {
          const initialPreferred =
            remoteHistory[0] ||
            pickPreferredScan(mergedHistory, [currentActive, storedActiveScanId, latestLocalOnly[0]?.id]);
          if (initialPreferred) {
            applyScanPayload(initialPreferred.prospects, initialPreferred.id, orgForSubscription);
          }
          scanHistoryHydratedRef.current = true;
          return;
        }

        if (!currentActiveExists) {
          const nextPreferred =
            remoteHistory[0] ||
            pickPreferredScan(mergedHistory, [storedActiveScanId, latestLocalOnly[0]?.id]);
          if (nextPreferred) {
            applyScanPayload(nextPreferred.prospects, nextPreferred.id, orgForSubscription);
          }
        }
      },
      (error) => {
        console.warn('Search history subscription failed:', error);
      },
      orgForSubscription
    );

    // Load data from Firebase
      const loadUserData = async () => {
        try {
          // Load call logs from Firebase
          try {
            const callLogs = await getUserCallLogs(currentUser.id, orgForSubscription);
            if (selectedOrgRef.current !== orgForSubscription) return;
            setLog(callLogs);
            writeOrgScopedJson(STORAGE_KEYS.callLogs, currentUser.id, orgForSubscription, callLogs);
            console.log('Loaded call logs from Firebase:', callLogs.length);
          } catch (e) {
            console.warn('Call logs load failed, falling back to localStorage');
          }
          
          // Load saved prospects from Firebase
          try {
            const savedV2 = await getUserSavedProspectsV2(currentUser.id, orgForSubscription);
            if (selectedOrgRef.current !== orgForSubscription) return;
            const normalized = mergeSavedProspectSources([], savedV2);
            setSavedProspects(normalized);
            writeOrgScopedJson(STORAGE_KEYS.savedProspects, currentUser.id, orgForSubscription, normalized);
            console.log('Loaded saved prospects from Firebase:', normalized.length);
            const meta: Record<string, ProspectRecord> = {};
            savedV2.forEach((p) => {
              meta[p.id] = p;
            });
            setSavedProspectMetaById(meta);
            // One-time migration: legacy savedProspects -> prospects(v2)
            void migrateLegacySavedProspectsToV2(currentUser.id, normalized);
          } catch (e) {
            console.warn('Saved prospects load failed, falling back to localStorage');
          }
          
          // Load blocklist from Firebase (universal system only)
          // Removed old per-user blocklist loading to avoid conflicts
          
          // Load all users from Firebase if Admin or SuperAdmin
          if (isAdmin()) {
            try {
              const firebaseUsers = await getAllUsers();
              setAllUsers(firebaseUsers);
              console.log('Loaded all users from Firebase:', firebaseUsers.length);
              localStorage.setItem('raedar_users', JSON.stringify(firebaseUsers));
            } catch (error) {
              console.error('Error loading users from Firebase:', error);
              // Fallback to any cached copy so the UI isn't empty
              const savedUsers = localStorage.getItem('raedar_users');
              if (savedUsers) {
                try {
                  setAllUsers(JSON.parse(savedUsers));
                } catch {
                  setAllUsers([]);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading user data from Firebase:', error);
          // Fallback to localStorage if Firebase fails
          const userLogKey = `raedar_call_logs_${currentUser.id}`;
          const savedLog = localStorage.getItem(userLogKey);
          if (savedLog) {
            try {
              const parsedLog = JSON.parse(savedLog);
              setLog(parsedLog);
            } catch (error) {
              console.error('Error parsing call logs:', error);
              setLog([]);
            }
          }
          
          const userSavedKey = `raedar_saved_prospects_${currentUser.id}`;
          const savedProspects = localStorage.getItem(userSavedKey);
          if (savedProspects) {
            try {
              const parsedSaved = dedupeSavedProspects(
                JSON.parse(savedProspects).map((item: Prospect) => normalizeProspectCategoryData(item))
              );
              setSavedProspects(parsedSaved);
              void migrateLegacySavedProspectsToV2(currentUser.id, parsedSaved);
            } catch (error) {
              console.error('Error parsing saved prospects:', error);
              setSavedProspects([]);
            }
          }
          if (!restoredFromLocal) {
            const fallbackHistory = filterScanHistoryForOrg(
              restoreScanHistoryFromLocal(currentUser.id, orgForSubscription),
              orgForSubscription
            );
            if (fallbackHistory.length > 0) {
              const preferredFallback = pickPreferredScan(fallbackHistory, [storedActiveScanId, activeScanId]);
              setScanHistory(fallbackHistory);
              if (preferredFallback) {
                applyScanPayload(preferredFallback.prospects, preferredFallback.id, orgForSubscription);
              }
            } else {
              restoreCachedScanResults(currentUser.id, orgForSubscription);
            }
          }
        }
      };
      
      loadUserData();
      void reloadOrgCrmData(currentUser.id, orgForSubscription);
      loadOrgScanWorkspace(currentUser.id, orgForSubscription);
      return () => unsubscribeSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, selectedOrg, orgPreferenceLoaded]);

  useEffect(() => {
    if (!currentUser || !canPersistOrgWorkspace(selectedOrg, scanResults, scanHistory)) return;
    writeOrgScopedJson(
      STORAGE_KEYS.scanHistory,
      currentUser.id,
      selectedOrg,
      filterScanHistoryForOrg(scanHistory, selectedOrg).slice(0, SCAN_HISTORY_STORE_LIMIT)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanHistory, currentUser, selectedOrg, scanResults]);

  useEffect(() => {
    if (!currentUser) return;
    writeOrgScopedJson(STORAGE_KEYS.activeScanId, currentUser.id, selectedOrg, activeScanId);
  }, [activeScanId, currentUser, selectedOrg]);

  const mapScanCoverageEntries: Array<{
    id: string;
    lat: number;
    lng: number;
    radiusMiles: number;
    createdAt: string;
    categories?: string[];
    locationLabel?: string;
    prospectCount?: number;
  }> = (showScanCoverage ? scanHistory : [])
    .filter((s) => {
      const ageMs = Date.now() - new Date(s.createdAt).getTime();
      return Number.isFinite(ageMs) && ageMs <= scanCoverageDays * 24 * 60 * 60 * 1000;
    })
    .reduce<Array<{
      id: string;
      lat: number;
      lng: number;
      radiusMiles: number;
      createdAt: string;
      categories?: string[];
      locationLabel?: string;
      prospectCount?: number;
    }>>((entries, s) => {
      const center = s.center || (s.prospects[0] ? { lat: s.prospects[0].lat, lng: s.prospects[0].lng } : undefined);
      if (!center) return entries;
      entries.push({
        id: s.id,
        lat: center.lat,
        lng: center.lng,
        radiusMiles: Number.isFinite(s.radius) && s.radius > 0 ? s.radius : radius,
        createdAt: s.createdAt,
        categories: s.categories,
        locationLabel: s.locationLabel,
        prospectCount: s.prospectCount
      });
      return entries;
    }, []);

  // Ensure users are loaded when opening the Admin tab (retry if initial attempt failed)
  useEffect(() => {
    const loadUsersIfAdmin = async () => {
      if (!isAdmin()) return;
      if (activeTab !== 'admin') return;
      try {
        // Always fetch a fresh list when opening Admin tab to avoid stale cache
        const firebaseUsers = await getAllUsers();
        setAllUsers(firebaseUsers);
        localStorage.setItem('raedar_users', JSON.stringify(firebaseUsers));
        console.log('✅ Admin tab load: users fetched:', firebaseUsers.length);
      } catch (error) {
        console.error('Admin tab: failed to load users:', error);
      }
    };
    loadUsersIfAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUser]);
  
  // Load org-scoped admin lists and shared app data
  useEffect(() => {
    console.log(`Loading org-scoped admin data for ${selectedOrg}...`);
    setBlocklistLoaded(false);

    const unsubscribeBlocklist = subscribeUniversalBlocklist((entries) => {
      setBlocklist(entries);
      setBlocklistLoaded(true);
    }, selectedOrg);

    const unsubscribeCurrentAdvertisers = subscribeCurrentAdvertisers((entries) => {
      const advertiserMap: Record<string, { businessName: string; markedByName: string; markedAt: Date }> = {};
      entries.forEach((entry: string) => {
        const parts = entry.split('|');
        if (parts.length >= 5) {
          const businessHash = parts[4];
          advertiserMap[businessHash] = {
            businessName: parts[0],
            markedByName: parts[2],
            markedAt: new Date(parts[3])
          };
        }
      });
      setCurrentAdvertisers(advertiserMap);
    }, selectedOrg);

    return () => {
      unsubscribeBlocklist();
      unsubscribeCurrentAdvertisers();
    };
  }, [selectedOrg]);

  useEffect(() => {
    setCacheStats(getCacheStats());

    const savedUsers = localStorage.getItem('raedar_users');
    if (savedUsers) {
      setAllUsers(JSON.parse(savedUsers));
    }
  }, []);

  // Save data to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('raedar_current_user', JSON.stringify(currentUser));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      lastOrgLoadedUserRef.current = null;
      setOrgPreferenceLoaded(false);
      return;
    }

    if (lastOrgLoadedUserRef.current !== currentUser.id) {
      lastOrgLoadedUserRef.current = currentUser.id;
      const storedOrg = localStorage.getItem(organizationStorageKey(currentUser.id));
      if (isValidOrganizationKey(storedOrg)) {
        setSelectedOrg(storedOrg);
      } else {
        setSelectedOrg(DEFAULT_ORGANIZATION);
      }
      previousOrgRef.current = isValidOrganizationKey(storedOrg) ? storedOrg : DEFAULT_ORGANIZATION;
    }

    setOrgPreferenceLoaded(true);
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    localStorage.setItem(organizationStorageKey(currentUser.id), selectedOrg);
  }, [selectedOrg, currentUser]);

  // Save user-specific data
  useEffect(() => {
    if (!currentUser || orgWorkspaceHydratingRef.current) return;
    if (log.length > 0 && log.some((entry) => !recordMatchesOrg(entry, selectedOrg))) return;
    writeOrgScopedJson(STORAGE_KEYS.callLogs, currentUser.id, selectedOrg, log);
  }, [log, currentUser, selectedOrg]);

  useEffect(() => {
    if (!currentUser || !prospectsBelongToOrg(savedProspects, selectedOrg)) return;
    writeOrgScopedJson(STORAGE_KEYS.savedProspects, currentUser.id, selectedOrg, savedProspects);
  }, [savedProspects, currentUser, selectedOrg]);

  // Save user-specific location and search parameters
  useEffect(() => {
    if (currentUser) {
      const userLocationKey = `raedar_location_${currentUser.id}`;
      const locationData = {
        userPos,
        currentLocation,
        radius,
        prospectCount
      };
      localStorage.setItem(userLocationKey, JSON.stringify(locationData));
    }
  }, [userPos, currentLocation, radius, prospectCount, currentUser]);

  // Save user-specific scan results
  useEffect(() => {
    if (!currentUser || scanResults.length === 0 || !canPersistOrgWorkspace(selectedOrg, scanResults, scanHistory)) return;
    writeOrgScopedJson(STORAGE_KEYS.scanResults, currentUser.id, selectedOrg, scanResults);
    console.log(`💾 Saved ${scanResults.length} scan results for user ${currentUser.id} (${selectedOrg})`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResults, currentUser, selectedOrg, scanHistory]);

  // Save user-specific all prospects data
  useEffect(() => {
    if (!currentUser || allAvailableProspects.length === 0 || !canPersistOrgWorkspace(selectedOrg, allAvailableProspects, scanHistory)) return;
    writeOrgScopedJson(STORAGE_KEYS.allProspects, currentUser.id, selectedOrg, allAvailableProspects);
    console.log(`💾 Saved ${allAvailableProspects.length} all prospects for user ${currentUser.id} (${selectedOrg})`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAvailableProspects, currentUser, selectedOrg, scanHistory]);

  // Save universal blocklist - TEMPORARILY DISABLED FOR STABILITY
  // useEffect(() => {
  //   // Only save if blocklist is loaded and we're not in loading state
  //   if (blocklistLoaded && !isBlocklistLoading && blocklist.length >= 0) {
  //     console.log('💾 Saving universal blocklist to Firebase:', blocklist.length, 'items');
  //     
  //     // Save to Firebase with error handling
  //     saveUniversalBlocklist(blocklist).catch(error => {
  //       console.error('❌ Error saving universal blocklist:', error);
  //       // Don't fallback to localStorage - keep Firebase as single source of truth
  //     });
  //   } else if (isBlocklistLoading) {
  //     console.log('⏭️ Skipping save during blocklist loading');
  //   }
  // }, [blocklist, blocklistLoaded, isBlocklistLoading]);

  // Calculate prospect score (fallback when AI is not available)
  const calculateProspectScore = (prospect: Prospect): number => {
    let score = 0;
    
    // Basic scoring based on category
    const categoryScores: Record<string, number> = {
      "Auto Dealer": 0.8,
      "HVAC": 0.7,
      "Orthodontics": 0.7,
      "Dental": 0.6,
      "Plumbing": 0.6,
      "Roofing": 0.6,
      "Other": 0.4
    };
    
    score += categoryScores[prospect.category] || categoryScores["Other"];
    
    // Bonus for having website
    if (prospect.website) score += 0.1;
    
    // Bonus for good rating
    if (prospect.rating && prospect.rating > 4.0) score += 0.1;
    
    return Math.min(1, score);
  };

  const refreshDueReminders = async () => {
    if (!currentUser) return;
    setIsLoadingDueReminders(true);
    try {
      const rows = await getDueProspectReminders(currentUser.id, undefined, selectedOrg);
      const mapped: DueReminderEntry[] = rows.map((r: any) => ({
        id: r.id,
        name: r.name || r.id,
        nextReminderAt: r.nextReminderAt,
        phone: r.phone,
        website: r.website
      }));
      setDueReminders(mapped);
    } catch (error) {
      console.error('Failed loading due reminders:', error);
    } finally {
      setIsLoadingDueReminders(false);
    }
  };

  const clearDueReminder = async (reminder: DueReminderEntry) => {
    if (!currentUser) return;
    const confirmed = window.confirm(`Remove the reminder for "${reminder.name}"?`);
    if (!confirmed) return;
    setClearingReminderId(reminder.id);
    try {
      await upsertProspect(currentUser.id, reminder.id, {
        userId: currentUser.id,
        nextReminderAt: null,
        updatedAt: new Date().toISOString()
      } as any);
      await Promise.all([
        refreshDueReminders(),
        refreshSavedProspectMeta(currentUser.id)
      ]);
    } catch (error) {
      console.error('Failed clearing reminder:', error);
      alert('Failed to remove reminder.');
    } finally {
      setClearingReminderId(null);
    }
  };

  const refreshSavedProspectMeta = async (userId: string) => {
    try {
      const savedV2 = await getUserSavedProspectsV2(userId, selectedOrg);
      const meta: Record<string, ProspectRecord> = {};
      savedV2.forEach((p) => {
        meta[p.id] = p;
      });
      setSavedProspectMetaById(meta);
    } catch (error) {
      console.warn('Failed loading saved callback metadata:', error);
      setSavedProspectMetaById({});
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    if (!(activeTab === 'activity' || activeTab === 'call-sheet' || activeTab === 'calendar')) return;
    void refreshDueReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, activeTab, selectedOrg]);

  useEffect(() => {
    if (!currentUser) return;
    if (activeTab !== 'saved') return;
    void refreshSavedProspectMeta(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, activeTab, savedProspects.length, selectedOrg]);

  const addReminderToTodaySheet = async (reminder: DueReminderEntry) => {
    if (!currentUser) return;
    try {
      const today = await getOrCreateTodayCallSheet(currentUser.id, selectedOrg);
      await addProspectToCallSheet(currentUser.id, today.id, reminder.id, selectedOrg);
      setActivityView('call-sheet');
      setActiveTab('activity');
      alert(`Added "${reminder.name}" to this week's call sheet.`);
    } catch (error) {
      console.error(error);
      alert("Could not add reminder prospect to this week's call sheet.");
    }
  };

  // Location helper functions
  const getLocationName = async (lat: number, lng: number): Promise<string> => {
    try {
      if (!window.google?.maps) {
        return 'Location not available';
      }

      const geocoder = new window.google.maps.Geocoder();
      const response = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results) {
            resolve(results);
          } else {
            reject(new Error('Geocoding failed'));
          }
        });
      });

      if (response && response[0]) {
        const addressComponents = response[0].address_components;
        const city = addressComponents.find(component => 
          component.types.includes('locality')
        )?.long_name;
        const state = addressComponents.find(component => 
          component.types.includes('administrative_area_level_1')
        )?.short_name;
        
        return city && state ? `${city}, ${state}` : 'Unknown Location';
      }
      
      return 'Unknown Location';
    } catch (error) {
      console.error('Error getting location name:', error);
      return 'Location not available';
    }
  };

  const useGPSLocation = async () => {
    if (!navigator.geolocation) {
      alert('GPS is not available on this device');
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const newPos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      setUserPos(newPos);
      
      // Get location name
      const locationName = await getLocationName(newPos.lat, newPos.lng);
      setCurrentLocation(locationName);
      
    } catch (error) {
      console.error('GPS error:', error);
      alert('Could not get GPS location. Please try again or enter an address manually.');
    }
  };

  const setLocationFromAddress = async () => {
    if (!addressInput.trim()) {
      alert('Please enter an address');
      return;
    }

    try {
      if (!window.google?.maps) {
        alert('Google Maps is not available');
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      const response = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: addressInput }, (results, status) => {
          if (status === 'OK' && results) {
            resolve(results);
          } else {
            reject(new Error('Address not found'));
          }
        });
      });

      if (response && response[0]) {
        const location = response[0].geometry.location;
        const newPos = {
          lat: location.lat(),
          lng: location.lng()
        };

        setUserPos(newPos);
        
        // Get location name
        const locationName = await getLocationName(newPos.lat, newPos.lng);
        setCurrentLocation(locationName);
        
        setAddressInput('');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Address not found. Please check the address and try again.');
    }
  };

  const handleMapClick = async (lat: number, lng: number) => {
    const newPos = { lat, lng };
    setUserPos(newPos);
    
    // Get location name
    const locationName = await getLocationName(lat, lng);
    setCurrentLocation(locationName);
  };

  // Get user location on app load
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserPos(newPos);
          
          // Get location name
          const locationName = await getLocationName(newPos.lat, newPos.lng);
          setCurrentLocation(locationName);
        },
        (error) => {
          console.log('GPS not available, using default location');
          // Keep default location if GPS fails
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    }
  }, []); // Empty dependency array - run once on mount

  // Ensure map has a valid center even if GPS fails
  useEffect(() => {
    if (!userPos.lat || !userPos.lng) {
      console.log('Setting default location for map');
      setUserPos({ lat: 33.5186, lng: -86.8104 }); // Birmingham, AL
      setCurrentLocation('Birmingham, AL');
    }
  }, [userPos]);

  // Scan for prospects
  // Debounced scan to prevent rapid repeat calls
  let scanCooldownUntil = 0;
  const debounceMs = Math.max(500, Number(process.env.REACT_APP_SCAN_DEBOUNCE_MS || '2000'));

  const runProspectScan = async ({
    radiusMiles,
    categories,
    candidateLimit,
    laneLabel,
    exhaustive = false
  }: {
    radiusMiles: number;
    categories: CategoryGroup[];
    candidateLimit?: number;
    laneLabel: string;
    exhaustive?: boolean;
  }) => {
    if (!currentUser) {
      alert("Please log in first");
      return;
    }

    const now = Date.now();
    if (now < scanCooldownUntil) {
      const waitMs = scanCooldownUntil - now;
      console.log(`⏳ Scan cooling down, wait ${Math.ceil(waitMs/100)/10}s`);
      return;
    }
    scanCooldownUntil = now + debounceMs;

    setProspectsLoaded(0);
    setHasMoreProspects(false);
    setAllAvailableProspects([]);
    setShowInstructions(false);
    setIsScanning(true);

    try {
      console.log(`🔍 Starting ${laneLabel} AI-powered prospect scan...`);

      const businesses = await searchRealBusinesses(userPos.lat, userPos.lng, radiusMiles, prospectCount, categories, {
        exhaustive
      });
      console.log(`📊 Found ${businesses.length} businesses from API`);
      
      // Remove duplicates based on name and location - Enhanced for chain businesses
      const uniqueBusinesses = businesses.filter((business, index, self) => {
        const isDuplicate = self.findIndex(b => {
          // Normalize names for comparison (remove common variations)
          const normalizeName = (name: string) => {
            return name.toLowerCase()
              .trim()
              .replace(/[^\w\s]/g, '') // Remove punctuation
              .replace(/\s+/g, ' ') // Normalize whitespace
              .replace(/\b(inc|llc|corp|corporation|company|co|ltd|limited)\b/g, ''); // Remove common business suffixes
          };
          
          const normalizedName1 = normalizeName(b.name);
          const normalizedName2 = normalizeName(business.name);
          const nameMatch = normalizedName1 === normalizedName2;
          
          // For chain businesses, check if they're the same brand (more aggressive matching)
          const isChainBusiness = (name: string) => {
            const chainKeywords = ['storage', 'space', 'self storage', 'public storage', 'extra space', 'life storage', 'cubesmart'];
            const normalizedName = normalizeName(name);
            return chainKeywords.some(keyword => normalizedName.includes(keyword));
          };
          
          // Check if locations are very close (within ~100 meters for regular, ~500 meters for chains)
          const latDiff = Math.abs(b.lat - business.lat);
          const lngDiff = Math.abs(b.lng - business.lng);
          const isChain = isChainBusiness(business.name) || isChainBusiness(b.name);
          const threshold = isChain ? 0.005 : 0.001; // Larger threshold for chain businesses
          const locationMatch = latDiff < threshold && lngDiff < threshold;
          
          // Check if addresses are similar (normalized)
          const normalizeAddress = (addr: string) => {
            return addr.toLowerCase()
              .trim()
              .replace(/[^\w\s]/g, '') // Remove punctuation
              .replace(/\s+/g, ' ') // Normalize whitespace
              .replace(/\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|way|pl|place)\b/g, ''); // Remove street suffixes
          };
          
          const normalizedAddr1 = normalizeAddress(b.address);
          const normalizedAddr2 = normalizeAddress(business.address);
          const addressMatch = normalizedAddr1 === normalizedAddr2 && normalizedAddr1.length > 0;
          
          // For chain businesses with same name, be more aggressive about location-based deduplication
          if (nameMatch && isChain) {
            return locationMatch; // Just check location for chains with same name
          }
          
          // Consider it a duplicate if:
          // 1. Names match exactly and locations are close, OR
          // 2. Names match exactly and addresses match, OR
          // 3. Locations are very close and addresses match
          return (nameMatch && locationMatch) || (nameMatch && addressMatch) || (locationMatch && addressMatch);
        });
        
        return isDuplicate === index; // Keep only the first occurrence
      });
      
      // Additional aggressive deduplication for exact address matches
      const finalUniqueBusinesses = uniqueBusinesses.filter((business, index, self) => {
        const isDuplicate = self.findIndex(b => {
          // For exact address matches, consider them duplicates regardless of name
          const normalizeAddress = (addr: string) => {
            return addr.toLowerCase()
              .trim()
              .replace(/[^\w\s]/g, '') // Remove punctuation
              .replace(/\s+/g, ' ') // Normalize whitespace
              .replace(/\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|way|pl|place)\b/g, ''); // Remove street suffixes
          };
          
          const normalizedAddr1 = normalizeAddress(b.address);
          const normalizedAddr2 = normalizeAddress(business.address);
          const addressMatch = normalizedAddr1 === normalizedAddr2 && normalizedAddr1.length > 0;
          
          // If addresses match exactly, it's a duplicate
          if (addressMatch) {
            console.log(`🔍 DUPLICATE FOUND: "${b.name}" and "${business.name}" have same address: ${b.address}`);
            return true;
          }
          
          return false;
        });
        
        return isDuplicate === index;
      });
      
      // Final deduplication pass - create a unique key for each business
      const seenKeys = new Set<string>();
      const ultraUniqueBusinesses = finalUniqueBusinesses.filter(business => {
        // Create a unique key based on name + address + coordinates
        const normalizeName = (name: string) => {
          return name.toLowerCase()
            .trim()
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, ' ')
            .replace(/\b(inc|llc|corp|corporation|company|co|ltd|limited)\b/g, '');
        };
        
        const normalizeAddress = (addr: string) => {
          return addr.toLowerCase()
            .trim()
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, ' ')
            .replace(/\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|way|pl|place)\b/g, '');
        };
        
        const key = `${normalizeName(business.name)}|${normalizeAddress(business.address)}|${business.lat.toFixed(6)}|${business.lng.toFixed(6)}`;
        
        if (seenKeys.has(key)) {
          console.log(`🔍 ULTRA DUPLICATE REMOVED: "${business.name}" at ${business.address}`);
          return false;
        }
        
        seenKeys.add(key);
        return true;
      });
      
      console.log(`🔍 Removed ${businesses.length - ultraUniqueBusinesses.length} duplicates. ${ultraUniqueBusinesses.length} unique businesses remaining.`);
      
      // Convert to ProspectData format for AI analysis
      const prospectData: ProspectData[] = ultraUniqueBusinesses.map((business: RealBusiness) => ({
        name: business.name,
        category: business.category || "Other",
        rating: business.rating,
        reviewCount: business.reviews,
        address: business.address || "",
        distance: Math.sqrt(
          Math.pow(business.lat - userPos.lat, 2) + Math.pow(business.lng - userPos.lng, 2)
        ) * 69, // Rough conversion to miles
        types: [business.category || "Other"],
        businessStatus: business.openNow ? "Open" : "Closed",
        website: business.website,
        phone: business.phone
      }));

      // Filter out blocked businesses before AI analysis using stable IDs (index-aligned)
      console.log('Current blocklist during scan (IDs):', blocklist);
      const blockedIdSet = new Set(blocklist);
      const filteredEntries = ultraUniqueBusinesses
        .map((biz, i) => ({ biz, prospect: prospectData[i] }))
        .filter(({ biz, prospect }) => {
          const placeId = biz.placeId || biz.id;
          const businessHash = aiService.generateBusinessHash(prospect, placeId);
          const stableId = placeId || businessHash;
          return stableId ? !blockedIdSet.has(stableId) : true;
        });
      const scanEntries = candidateLimit ? filteredEntries.slice(0, candidateLimit) : filteredEntries;
      const filteredProspectData = scanEntries.map((e) => e.prospect);
      console.log('Prospects before filtering:', prospectData.length, 'After filtering:', filteredProspectData.length);

      // Use AI service for sophisticated analysis (with fallback)
      console.log('🤖 Starting AI analysis...');
      
      // Extract place IDs for caching (index-aligned)
      const placeIds = scanEntries
        .map(({ biz }) => biz.placeId)
        .filter((placeId): placeId is string => !!placeId);
      
      let aiAnalyses: AIProspectAnalysis[] = [];
      let aiAvailable = true;
      
      try {
        aiAnalyses = await aiService.analyzeProspectsBatch(filteredProspectData, placeIds, { orgKey: selectedOrg });
        console.log(`✅ AI analysis completed for ${aiAnalyses.length} prospects`);
      } catch (error) {
        console.warn('⚠️ AI analysis failed, using fallback scoring:', error);
        aiAvailable = false;
      }

      // Combine business data with AI analysis or fallback scoring (index-aligned)
      const prospects: Prospect[] = scanEntries.map(({ biz, prospect }, index) => {
        const aiAnalysis = aiAnalyses[index];
        const business = biz;

        // Use AI score if available, otherwise use fallback
        let score = 0;
        if (aiAvailable && aiAnalysis) {
          score = aiAnalysis.score / 100; // Convert from 0-100 to 0-1
        } else {
          // Fallback to basic scoring
          const fallbackProspect: Prospect = {
            id: business?.placeId || business?.id || `prospect_${index}`,
            name: prospect.name,
            category: prospect.category,
            categoryGroup: business?.categoryGroup,
            address: prospect.address,
            city: business?.city || "",
            state: business?.state || "",
            zip: business?.zip || "",
            phone: prospect.phone || "",
            website: prospect.website || "",
            lat: business?.lat || userPos.lat,
            lng: business?.lng || userPos.lng,
            rating: prospect.rating,
            reviews: prospect.reviewCount || 0,
            openNow: business?.openNow || true,
            placeId: business?.placeId
          };
          score = calculateProspectScore(fallbackProspect);
        }
        
        return tagProspectForOrg({
          id: business?.placeId || business?.id || `prospect_${index}`,
          name: prospect.name,
          category: prospect.category,
          categoryGroup: business?.categoryGroup,
          address: prospect.address,
          city: business?.city || "",
          state: business?.state || "",
          zip: business?.zip || "",
          phone: prospect.phone || "",
          website: prospect.website || "",
          lat: business?.lat || userPos.lat,
          lng: business?.lng || userPos.lng,
          rating: prospect.rating,
          reviews: prospect.reviewCount || 0,
          openNow: business?.openNow || true,
          placeId: business?.placeId,
          score: score,
          aiAnalysis: aiAvailable && aiAnalysis ? aiAnalysis : null
        }, selectedOrg);
      });

      // De-dup by id and sort by AI score
      const seenIds = new Set<string>();
      const uniqueProspects = prospects.filter(p => {
        if (!p.id) return false;
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
      });
      const scoredProspects = uniqueProspects
        .sort((a, b) => (b.score || 0) - (a.score || 0));

      // Store all prospects for admin view and pagination
      setAllProspects(scoredProspects);
      setAllAvailableProspects(scoredProspects);
      
      // Show initial results based on user slider setting
      const initialResults = scoredProspects.slice(0, prospectCount);
      scanResultsRef.current = initialResults;
      setScanResults(initialResults);
      setProspectsLoaded(prospectCount);
      
      // Check if there are more prospects available (up to 100 total)
      const maxProspects = Math.min(100, scoredProspects.length);
      setHasMoreProspects(prospectCount < maxProspects);

      // Update in-memory scan history immediately.
      const historyLocation = currentLocation || `${userPos.lat.toFixed(4)}, ${userPos.lng.toFixed(4)}`;
      const tempHistoryId = `local_${Date.now()}`;
      const historyItem: ScanHistoryItem = {
        id: tempHistoryId,
        orgKey: selectedOrg,
        createdAt: new Date().toISOString(),
        locationLabel: historyLocation,
        radius: radiusMiles,
        center: { lat: userPos.lat, lng: userPos.lng },
        categories,
        prospectCount: scoredProspects.length,
        prospects: scoredProspects
      };
      setActiveScanId(tempHistoryId);
      const nextHistory = [historyItem, ...scanHistoryRef.current.filter((s) => s.id !== tempHistoryId)].slice(0, SCAN_HISTORY_STORE_LIMIT);
      scanHistoryRef.current = nextHistory;
      setScanHistory(nextHistory);

      // Persist full scan payload for reliable restore on app return/reload
      if (currentUser) {
        void saveProspects(currentUser.id, scoredProspects, {
          operatingMode: operatingModeForOrganization(selectedOrg),
          createdAt: new Date().toISOString(),
          locationName: historyLocation,
          radius: radiusMiles,
          prospectCount,
          categories,
          location: userPos,
          orgKey: selectedOrg
        }, selectedOrg).then((savedSearchId) => {
          setActiveScanId(savedSearchId);
          setScanHistory((prev) => {
            const upgraded: ScanHistoryItem = { ...historyItem, id: savedSearchId };
            return [upgraded, ...prev.filter((s) => s.id !== tempHistoryId && s.id !== savedSearchId)].slice(0, SCAN_HISTORY_STORE_LIMIT);
          });
          console.log(`💾 Saved scan to Firebase: ${scoredProspects.length} prospects`);
        }).catch((e) => {
          console.warn("Failed to save scan to Firebase, local cache still available:", e);
        });
      }
      
      console.log(`🎯 Scan complete: ${initialResults.length} prospects ready for review`);
      console.log(`📊 Total available: ${scoredProspects.length}, Loaded: ${prospectCount}, Max: ${maxProspects}`);
    } catch (error) {
      console.error("Scan error:", error);
      alert("Error scanning for businesses. Check console for details.");
    } finally {
      setIsScanning(false);
    }
  };

  const handleScan = async () => {
    if (selectedCategories.length === 0) {
      alert('Select at least one industry before scanning.');
      return;
    }
    await runProspectScan({
      radiusMiles: radius,
      categories: selectedCategories,
      laneLabel: 'local'
    });
  };

  const handleVerticalScan = async () => {
    await runProspectScan({
      radiusMiles: verticalRadius,
      categories: [verticalCategory],
      candidateLimit: MAX_VERTICAL_SCAN_CANDIDATES,
      laneLabel: 'vertical',
      exhaustive: true
    });
  };

  const removeScanHistoryEntry = async (scanId: string) => {
    if (!currentUser) return;
    const confirmed = window.confirm('Remove this scan from history and clear its coverage marker?');
    if (!confirmed) return;

    const nextHistory = scanHistory.filter((scan) => scan.id !== scanId);
    setScanHistory(nextHistory);

    if (activeScanId === scanId) {
      const nextActive = nextHistory[0] || null;
      if (nextActive) {
        applyScanPayload(nextActive.prospects, nextActive.id);
      } else {
        setActiveScanId(null);
        setScanResults([]);
        setAllProspects([]);
        setAllAvailableProspects([]);
        setProspectsLoaded(0);
        setHasMoreProspects(false);
      }
    }

    const activeScanStorageKey = `raedar_active_scan_id_${currentUser.id}`;
    if (activeScanId === scanId) {
      if (nextHistory[0]?.id) localStorage.setItem(activeScanStorageKey, nextHistory[0].id);
      else localStorage.removeItem(activeScanStorageKey);
    }
    localStorage.setItem(`raedar_scan_history_${currentUser.id}`, JSON.stringify(nextHistory));

    if (!scanId.startsWith('local_')) {
      try {
        await deleteUserSearch(scanId);
      } catch (error) {
        console.error('Failed deleting scan history entry from Firebase:', error);
      }
    }
  };

  // Load more prospects
  const handleLoadMore = () => {
    if (!hasMoreProspects || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const currentLoaded = prospectsLoaded;
      const nextBatch = Math.min(prospectCount, 100 - currentLoaded);
      const startIndex = currentLoaded;
      const endIndex = startIndex + nextBatch;
      
      const newProspects = allAvailableProspects.slice(startIndex, endIndex);
      setScanResults(prev => [...prev, ...newProspects]);
      setProspectsLoaded(currentLoaded + nextBatch);
      
      // Check if we've reached the limit
      const newTotal = currentLoaded + nextBatch;
      setHasMoreProspects(newTotal < Math.min(100, allAvailableProspects.length));
      
      console.log(`📄 Loaded ${nextBatch} more prospects. Total: ${newTotal}/${Math.min(100, allAvailableProspects.length)}`);
    } catch (error) {
      console.error("Error loading more prospects:", error);
      alert("Error loading more prospects. Please try again.");
    } finally {
      setIsLoadingMore(false);
    }
  };
  const logCall = async (prospect: Prospect, outcome: OutcomeType, note: string) => {
    if (outcome === "Saved for Later") {
      const normalizedProspect = normalizeProspectCategoryData(prospect);
      setSavedProspects(prev => {
        const exists = prev.some(p => p.id === normalizedProspect.id);
        if (!exists) return [...prev, normalizedProspect];
        return prev.map((p) => (p.id === normalizedProspect.id ? { ...p, ...normalizedProspect } : p));
      });
      if (currentUser) {
        try {
          await saveProspect(currentUser.id, normalizedProspect);
          await ensureProspectV2(currentUser.id, normalizedProspect, true);
          void refreshSavedProspectMeta(currentUser.id);
        } catch (e) {
          console.warn('Failed to save prospect to Firebase, local only');
        }
      }
    } else {
      const logEntry: CallLogEntry = tagWithOrg({
        id: Date.now().toString(),
        name: prospect.name,
        category: prospect.category,
        phone: prospect.phone,
        website: prospect.website,
        address: prospect.address,
        timestamp: new Date().toISOString(),
        outcome,
        note
      }, resolveOrgKey(prospect.orgKey || selectedOrg));
      setLog(prev => [logEntry, ...prev]);
      
      // Save to Firebase
      if (currentUser) {
        try {
          await saveCallLog(currentUser.id, prospect.id, logEntry, resolveOrgKey(prospect.orgKey || selectedOrg));
        } catch (error) {
          console.error('Error saving call log to Firebase:', error);
          // Fallback handled by local state + persistence effect
        }
      }
    }
  };

  // Generate appointment strategy for a prospect
  const generateAppointmentStrategy = async (prospect: Prospect) => {
    setIsGeneratingStrategy(true);
    setShowAppointmentModal(true);
    
    try {
      // Convert Prospect to ProspectData format for AI service with real distance
      const toMiles = (aLat: number, aLng: number, bLat: number, bLng: number) => {
        const toRad = (v: number) => (v * Math.PI) / 180;
        const dLat = toRad(bLat - aLat);
        const dLng = toRad(bLng - aLng);
        const R = 3958.8;
        const h = Math.sin(dLat/2)**2 + Math.cos(toRad(aLat))*Math.cos(toRad(bLat))*Math.sin(dLng/2)**2;
        return 2 * R * Math.asin(Math.sqrt(h));
      };
      const distanceMi = toMiles(userPos.lat, userPos.lng, prospect.lat, prospect.lng);
      const prospectData: ProspectData = {
        name: prospect.name,
        category: prospect.category,
        rating: prospect.rating,
        reviewCount: prospect.reviews,
        address: prospect.address,
        distance: distanceMi,
        types: [prospect.category],
        website: prospect.website,
        phone: prospect.phone
      };
      
      const strategy = await aiService.generateAppointmentStrategy(prospectData, {
        orgKey: selectedOrg,
        liteScan: prospect.gemmetricLiteScan
      });
      setAppointmentStrategy(strategy);
    } catch (error) {
      console.error('Failed to generate appointment strategy:', error);
      alert('Failed to generate appointment strategy. Please try again.');
      setShowAppointmentModal(false);
    } finally {
      setIsGeneratingStrategy(false);
    }
  };

  // Defensive renderer for AI payloads (prevents React object-child crashes)
  const strategyText = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((v) => strategyText(v)).filter(Boolean).join(' | ');
    if (typeof value === 'object') {
      if (typeof value.text === 'string') return value.text;
      if (typeof value.why === 'string') return value.why;
      if (typeof value.question === 'string') return value.question;
      if (typeof value.response === 'string') return value.response;
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const isGemmetricOrg = selectedOrg === 'gemmetric';

  const formatProspectScore = (prospect: Prospect): string => {
    const value = Math.round((prospect.score || 0) * 100);
    return isGemmetricOrg ? String(value) : `${value}%`;
  };

  const updateProspectEverywhere = (prospectId: string, updater: (prospect: Prospect) => Prospect) => {
    setScanResults((prev) => {
      const next = prev.map((p) => (p.id === prospectId ? updater(p) : p));
      scanResultsRef.current = next;
      return next;
    });
    setAllAvailableProspects((prev) => prev.map((p) => (p.id === prospectId ? updater(p) : p)));
    setAllProspects((prev) => prev.map((p) => (p.id === prospectId ? updater(p) : p)));
    setSavedProspects((prev) => prev.map((p) => (p.id === prospectId ? updater(p) : p)));
  };

  const runLiteScanForProspect = async (prospect: Prospect) => {
    let targetWebsite = prospect.website;
    if (!targetWebsite) {
      try {
        const details = await fetchProspectDetailsOnDemand(prospect);
        targetWebsite = details.website || '';
        if (targetWebsite) {
          updateProspectEverywhere(prospect.id, (p) => ({ ...p, ...details, website: targetWebsite }));
        }
      } catch (e) {
        console.warn('Lite scan website lookup failed', e);
      }
    }

    if (!targetWebsite) {
      alert('Website not available for Lite Scan.');
      return;
    }

    setLiteScanLoadingById((prev) => ({ ...prev, [prospect.id]: true }));
    try {
      const result = await runGemmetricLiteScan(targetWebsite);
      updateProspectEverywhere(prospect.id, (p) => ({ ...p, gemmetricLiteScan: result }));
    } catch (error: any) {
      console.error('Lite scan failed:', error);
      alert(error?.message || 'Lite scan failed. Please try again.');
    } finally {
      setLiteScanLoadingById((prev) => ({ ...prev, [prospect.id]: false }));
    }
  };

  const renderProspectAiInsights = (prospect: Prospect) => {
    if (!prospect.aiAnalysis) return null;
    return (
      <div style={styles.aiAnalysisContainer}>
        <div style={styles.aiAnalysisHeader}>
          <span style={styles.aiAnalysisIcon}>🤖</span>
          <span style={styles.aiAnalysisTitle}>{ORGANIZATION_ANALYSIS_TITLES[selectedOrg]}</span>
          <span style={styles.aiConfidence}>
            {analysisConfidenceText(prospect.aiAnalysis.confidence)}
          </span>
        </div>

        <div style={styles.aiReasoning}>
          <strong style={styles.aiReasoningStrong}>{ORGANIZATION_REASONING_LABELS[selectedOrg]}:</strong>
          <p style={styles.aiReasoningParagraph}>{strategyText(prospect.aiAnalysis.reasoning)}</p>
        </div>

        {prospect.aiAnalysis.recommendations && Array.isArray(prospect.aiAnalysis.recommendations) && prospect.aiAnalysis.recommendations.length > 0 && (
          <div style={styles.aiRecommendations}>
            <strong style={styles.aiRecommendationsStrong}>{ORGANIZATION_RECOMMENDATIONS_LABELS[selectedOrg]}:</strong>
            <ul style={styles.recommendationsList}>
              {prospect.aiAnalysis.recommendations.map((rec, index) => (
                <li key={index} style={styles.recommendationItem}>
                  {strategyText(rec)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderGemmetricLiteScanPrompt = (prospect: Prospect) => {
    if (!isGemmetricOrg || prospect.gemmetricLiteScan) return null;
    const loading = !!liteScanLoadingById[prospect.id];
    return (
      <div style={styles.liteScanPrompt}>
        <p style={styles.liteScanPromptText}>
          Check the business website for AI visibility signals before your call.
        </p>
        <button
          style={{
            ...styles.liteScanCtaButton,
            opacity: loading ? 0.7 : 1
          }}
          onClick={() => void runLiteScanForProspect(prospect)}
          disabled={loading}
          title="Run lightweight website diagnostics"
        >
          {loading ? '🔎 Scanning website...' : '🔎 Run Lite Scan'}
        </button>
      </div>
    );
  };

  const renderLiteScanInsights = (prospect: Prospect) => {
    if (!isGemmetricOrg || !prospect.gemmetricLiteScan) return null;
    const scan = enrichLiteScanForDisplay(prospect.gemmetricLiteScan);
    const { displaySnapshot, scoreInterpretation } = scan;
    const opportunityLevel = scan.opportunityLevel || scoreInterpretation.opportunityLevel;

    return (
      <div style={styles.liteScanContainer}>
        <div style={styles.liteScanHeader}>
          <span style={styles.liteScanIcon}>🔎</span>
          <div style={styles.liteScanTitleBlock}>
            <span style={styles.liteScanTitle}>AI Visibility Snapshot</span>
            <span style={styles.liteScanSubtitle}>Initial assessment · preliminary findings only</span>
          </div>
          {typeof scan.opportunityScore === 'number' && (
            <div style={styles.liteScanScoreBlock}>
              <span style={styles.liteScanScore}>Opportunity {scan.opportunityScore}</span>
              <span style={styles.liteScanScoreTier}>{scoreInterpretation.tier}</span>
            </div>
          )}
          <span style={styles.liteScanOpportunityBadge}>
            {formatOpportunityLevelLabel(opportunityLevel)}
          </span>
        </div>

        {scan.summary && <p style={styles.liteScanSummary}>{scan.summary}</p>}
        {scoreInterpretation.description && (
          <p style={styles.liteScanScoreDescription}>{scoreInterpretation.description}</p>
        )}

        {scan.prospectingInsights && scan.prospectingInsights.length > 0 && (
          <div style={styles.liteScanProspectingSection}>
            <strong style={styles.liteScanProspectingHeading}>Why This May Matter</strong>
            <ul style={styles.liteScanProspectingList}>
              {scan.prospectingInsights.map((item, index) => (
                <li key={`prospecting-${index}`} style={styles.liteScanProspectingItem}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(displaySnapshot.strengths.length > 0 || displaySnapshot.opportunities.length > 0) && (
          <div style={styles.liteScanSnapshotSection}>
            {displaySnapshot.strengths.length > 0 && (
              <div style={styles.liteScanSnapshotGroup}>
                <strong style={styles.liteScanSnapshotHeading}>Strengths</strong>
                <ul style={styles.liteScanSnapshotList}>
                  {displaySnapshot.strengths.map((item, index) => (
                    <li key={`strength-${index}`} style={styles.liteScanSnapshotStrength}>
                      ✅ {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {displaySnapshot.opportunities.length > 0 && (
              <div style={styles.liteScanSnapshotGroup}>
                <strong style={styles.liteScanSnapshotHeading}>Potential Opportunities</strong>
                <ul style={styles.liteScanSnapshotList}>
                  {displaySnapshot.opportunities.map((item, index) => (
                    <li key={`opportunity-${index}`} style={styles.liteScanSnapshotOpportunity}>
                      ⚠️ {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {scan.findings?.length > 0 && (
          <div style={styles.liteScanTechnicalSection}>
            <strong style={styles.liteScanTechnicalHeading}>Supporting Evidence</strong>
            <ul style={styles.liteScanFindingsList}>
              {scan.findings.map((finding) => (
                <li key={finding.id} style={styles.liteScanFindingItem}>
                  <div style={styles.liteScanFindingTitle}>
                    <strong>{finding.businessLabel || finding.label}</strong>
                    <span style={styles.liteScanFindingConfidence}>
                      {formatFindingConfidenceLabel(finding.confidence)}
                    </span>
                  </div>
                  {finding.whyItMatters && (
                    <p style={styles.liteScanFindingWhy}>{finding.whyItMatters}</p>
                  )}
                  {finding.detail && (
                    <p style={styles.liteScanFindingDetail}>{finding.detail}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          style={{
            ...styles.liteScanRerunButton,
            opacity: liteScanLoadingById[prospect.id] ? 0.7 : 1
          }}
          onClick={() => void runLiteScanForProspect(prospect)}
          disabled={!!liteScanLoadingById[prospect.id]}
          title="Re-run lightweight website diagnostics"
        >
          {liteScanLoadingById[prospect.id] ? '🔎 Re-scanning...' : '🔎 Re-run Lite Scan'}
        </button>
      </div>
    );
  };

  // Defensive helpers for scan-result AI/advertiser rendering
  const analysisConfidenceText = (value: any): string => {
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(n)) return `${Math.max(0, Math.min(100, Math.round(n)))}% confidence`;
    return 'N/A confidence';
  };

  const advertiserDateText = (value: any): string => {
    try {
      if (value && typeof value.toDate === 'function') {
        return formatMdy(value.toDate());
      }
      if (value instanceof Date) {
        return formatMdy(value);
      }
      if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return formatMdy(parsed);
      }
    } catch (e) {
      // Fall through to default below
    }
    return 'Unknown date';
  };

  // Lazy details fetch helpers to avoid broad prefetch costs
  const isLikelyGooglePlaceId = (id?: string): boolean => {
    if (!id) return false;
    return !(
      id.startsWith("osm_") ||
      id.startsWith("yelp_") ||
      id.startsWith("mock_") ||
      id.startsWith("prospect_")
    );
  };

  const resolveGooglePlaceIdForProspect = async (prospect: Prospect): Promise<string | null> => {
    if (isLikelyGooglePlaceId(prospect.placeId)) return prospect.placeId || null;
    if (isLikelyGooglePlaceId(prospect.id)) return prospect.id;

    try {
      // On-demand lookup only when user clicks an action button
      const lookedUp = await lookupBusinessByName(prospect.name, prospect.lat, prospect.lng, Math.max(0.5, radius));
      if (lookedUp?.placeId && isLikelyGooglePlaceId(lookedUp.placeId)) return lookedUp.placeId;
      if (lookedUp?.id && isLikelyGooglePlaceId(lookedUp.id)) return lookedUp.id;
    } catch (e) {
      console.warn("On-demand place ID lookup failed:", e);
    }
    return null;
  };

  const fetchProspectDetailsOnDemand = async (prospect: Prospect): Promise<Partial<Prospect>> => {
    const googlePlaceId = await resolveGooglePlaceIdForProspect(prospect);
    if (!googlePlaceId) return {};
    try {
      const details = await getPlaceDetails(googlePlaceId);
      return {
        placeId: googlePlaceId,
        phone: details.phone || prospect.phone,
        website: details.website || prospect.website,
        address: details.address || prospect.address,
        city: details.city || prospect.city,
        state: details.state || prospect.state,
        zip: details.zip || prospect.zip
      };
    } catch (e) {
      console.warn("On-demand place details fetch failed:", e);
      return {};
    }
  };

  const getProspectStableId = (p: Prospect): { id: string; placeId?: string; businessHash?: string } => {
    const candidatePlaceId = (p as any).placeId || (isLikelyGooglePlaceId(p.id) ? p.id : undefined);
    if (isLikelyGooglePlaceId(candidatePlaceId)) return { id: candidatePlaceId, placeId: candidatePlaceId };
    const businessHash = aiService.generateBusinessHash({
      name: p.name,
      category: p.category || 'Business',
      address: p.address || '',
      distance: 0,
      types: [p.category || 'Business'],
      businessStatus: p.openNow ? 'Open' : 'Closed',
      website: p.website,
      phone: p.phone
    });
    return { id: businessHash, businessHash };
  };

  const ensureProspectV2 = async (userId: string, p: Prospect, saved: boolean) => {
    const normalizedProspect = normalizeProspectCategoryData(p);
    const prospectOrg = resolveOrgKey(p.orgKey || selectedOrg);
    const { id, placeId, businessHash } = getProspectStableId(normalizedProspect);
    const scopedId = orgProspectDocId(prospectOrg, id);
    const group = normalizedProspect.categoryGroup;
    await upsertProspect(userId, scopedId, {
      placeId,
      businessHash,
      orgKey: prospectOrg,
      name: normalizedProspect.name,
      address: normalizedProspect.address || '',
      city: normalizedProspect.city || '',
      state: normalizedProspect.state || '',
      zip: normalizedProspect.zip || '',
      lat: normalizedProspect.lat,
      lng: normalizedProspect.lng,
      categoryGroup: group as any,
      categoryLabel: normalizedProspect.category,
      phone: normalizedProspect.phone,
      website: normalizedProspect.website,
      gemmetricLiteScan: normalizedProspect.gemmetricLiteScan,
      saved,
      status: saved ? 'saved' : 'working',
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    } as any);
    return scopedId;
  };

  const getSavedProspectMeta = (p: Prospect): ProspectRecord | undefined => {
    const stableId = getProspectStableId(p).id;
    const scopedId = orgProspectDocId(resolveOrgKey(p.orgKey || selectedOrg), stableId);
    return savedProspectMetaById[scopedId] || savedProspectMetaById[stableId];
  };

  const getProspectCategoryGroup = (p: Prospect): CategoryGroup | undefined => {
    const fromVisibleLabel = inferCategoryGroupFromText(p.category);
    if (fromVisibleLabel) return fromVisibleLabel;

    const direct = p.categoryGroup;
    if (direct) return direct;

    const meta = getSavedProspectMeta(p);
    const fromMetaLabel = inferCategoryGroupFromText(meta?.categoryLabel);
    if (fromMetaLabel) return fromMetaLabel;

    return meta?.categoryGroup;
  };

  const matchesSavedCategoryFilter = (p: Prospect): boolean => {
    if (savedCategoryFilter === 'all') return true;
    return getProspectCategoryGroup(p) === savedCategoryFilter;
  };

  useEffect(() => {
    let mounted = true;
    const loadCurrentWeekSheetProspects = async () => {
      if (!currentUser) {
        if (mounted) setCurrentWeekSheetProspectIds({});
        return;
      }
      try {
        const sheet = await getOrCreateTodayCallSheet(currentUser.id, selectedOrg);
        const rows = await listCallSheetItems(currentUser.id, sheet.id);
        if (!mounted) return;
        const next: Record<string, boolean> = {};
        rows.forEach((row) => {
          if (row.prospectId) next[row.prospectId] = true;
        });
        setCurrentWeekSheetProspectIds(next);
      } catch (error) {
        console.warn('Failed loading current week call sheet membership:', error);
      }
    };
    void loadCurrentWeekSheetProspects();
    return () => {
      mounted = false;
    };
  }, [currentUser, selectedOrg]);

  const isProspectInCurrentWeekSheet = (prospect: Prospect): boolean => {
    const stableId = getProspectStableId(prospect).id;
    const scopedId = orgProspectDocId(resolveOrgKey(prospect.orgKey || selectedOrg), stableId);
    return !!currentWeekSheetProspectIds[scopedId] || !!currentWeekSheetProspectIds[stableId];
  };

  const callbackSavedProspects = savedProspects.filter((p) => Boolean(getSavedProspectMeta(p)?.callbackPending));
  const callbackDueSavedProspects = callbackSavedProspects.filter((p) => {
    const dueAt = getSavedProspectMeta(p)?.nextReminderAt;
    if (!dueAt) return true;
    return new Date(dueAt).getTime() <= Date.now();
  });
  const filteredSavedProspects = savedProspects.filter(matchesSavedCategoryFilter);
  const filteredCallbackSavedProspects = callbackSavedProspects.filter(matchesSavedCategoryFilter);
  const filteredCallbackDueSavedProspects = callbackDueSavedProspects.filter(matchesSavedCategoryFilter);
  const visibleSavedProspects = savedViewFilter === 'callbacks' ? filteredCallbackSavedProspects : filteredSavedProspects;

  const togglePrimaryCategory = (group: CategoryGroup, checked: boolean) => {
    setSelectedCategories((prev) => {
      if (!checked) return prev.filter((value) => value !== group);
      if (prev.includes(group) || prev.length >= MAX_PRIMARY_SCAN_CATEGORIES) return prev;
      return [...prev, group];
    });
  };

  const addProspectCardToTodaySheet = async (prospect: Prospect, saved: boolean) => {
    if (!currentUser) {
      alert('Please log in first');
      return;
    }
    const stableProspectId = getProspectStableId(prospect).id;
    const scopedProspectId = orgProspectDocId(resolveOrgKey(prospect.orgKey || selectedOrg), stableProspectId);
    if (currentWeekSheetProspectIds[scopedProspectId] || currentWeekSheetProspectIds[stableProspectId]) return;
    setAddingToWeekByProspectId((prev) => ({ ...prev, [stableProspectId]: true }));
    try {
      const sheet = await getOrCreateTodayCallSheet(currentUser.id, selectedOrg);
      const prospectId = await ensureProspectV2(currentUser.id, prospect, saved);
      await addProspectToCallSheet(currentUser.id, sheet.id, prospectId, selectedOrg);
      setCurrentWeekSheetProspectIds((prev) => ({ ...prev, [stableProspectId]: true, [prospectId]: true, [scopedProspectId]: true }));
    } catch (e) {
      console.error(e);
      alert('Failed adding to call sheet. Check console for details.');
    } finally {
      setAddingToWeekByProspectId((prev) => {
        const next = { ...prev };
        delete next[stableProspectId];
        return next;
      });
    }
  };

  const closeInstructions = async () => {
    console.log('closeInstructions called, dontShowAgain:', dontShowAgain, 'currentUser:', currentUser?.id);
    setShowInstructions(false);
    if (dontShowAgain && currentUser) {
      try {
        console.log('Saving hideInstructions: true to Firebase for user:', currentUser.id);
        // Save to Firebase
        await updateUserPreferences(currentUser.id, { hideInstructions: true });
        // Also save to localStorage as fallback
        localStorage.setItem('raedar_hide_instructions', 'true');
        console.log('Successfully saved hideInstructions preference');
      } catch (error) {
        console.error('Error saving instruction preference:', error);
        // Fallback to localStorage only
        localStorage.setItem('raedar_hide_instructions', 'true');
      }
    } else {
      console.log('Not saving preference - dontShowAgain:', dontShowAgain, 'currentUser:', !!currentUser);
    }
  };

  const removeFromSavedByAnyIds = async (candidateIds: string[]) => {
    const ids = Array.from(new Set(candidateIds.filter((id) => typeof id === 'string' && id.length > 0)));
    if (ids.length === 0) return;
    setSavedProspects((prev) => prev.filter((p) => !ids.includes(p.id)));
    if (!currentUser) return;
    await Promise.all(
      ids.map((id) =>
        removeSavedProspectByUserAndBusiness(currentUser.id, id).catch(() => {
          console.warn('Failed to remove saved prospect in Firebase');
        })
      )
    );
  };

  const deleteCallLogEntry = (logEntryId: string) => {
    setLog(prev => prev.filter(entry => entry.id !== logEntryId));
  };

  const testDataPersistence = () => {
    if (currentUser) {
      console.log('Testing data persistence for user:', currentUser.id);
      
      // Add a test call log entry
      const testLogEntry: CallLogEntry = {
        id: Date.now().toString(),
        name: 'Test Business',
        category: 'Test Category',
        phone: '555-1234',
        website: 'https://test.com',
        address: '123 Test St, Test City, AL',
        timestamp: new Date().toISOString(),
        outcome: 'Called',
        note: 'Test call log entry'
      };
      setLog(prev => [testLogEntry, ...prev]);
      
      // Add a test saved prospect
      const testProspect: Prospect = {
        id: 'test-prospect-' + Date.now(),
        name: 'Test Saved Prospect',
        category: 'Test Category',
        phone: '555-5678',
        website: 'https://testsaved.com',
        address: '456 Test Ave, Test City, AL',
        city: 'Test City',
        state: 'AL',
        zip: '12345',
        lat: 33.5207,
        lng: -86.8025,
        score: 85,
        openNow: true,
        reviews: 0
      };
      setSavedProspects(prev => [testProspect, ...prev]);
      
      console.log('Added test data - check localStorage and refresh to verify persistence');
    }
  };

  // Admin helper functions
  const isAdmin = () => currentUser?.role === 'admin' || currentUser?.role === 'SuperAdmin';
  const isSuperAdmin = () => currentUser?.role === 'SuperAdmin';

  const addToBlocklist = async (businessName: string, prospectId?: string) => {
    console.log('=== ADDING TO UNIVERSAL BLOCKLIST ===');
    console.log('Business name:', businessName);
    console.log('Prospect ID:', prospectId);
    console.log('Current blocklist before adding (IDs):', blocklist);

    // Resolve stable ID for this business
    const candidate = allAvailableProspects.find(p => p.id === prospectId) ||
      allProspects.find(p => p.id === prospectId) ||
      allProspects.find(p => p.name === businessName) ||
      scanResults.find(p => p.name === businessName);

    const placeId = candidate?.id; // our ids are placeId/unique ids
    const businessHash = candidate?.aiAnalysis?.businessHash;
    const stableId = placeId || businessHash;
    if (!stableId) {
      console.warn('Could not resolve stable ID for blocklist; skipping');
      return;
    }

    const newBlocklist = Array.from(new Set([...blocklist, stableId]));
    setBlocklist(newBlocklist);
    try {
      await saveUniversalBlocklist(newBlocklist, selectedOrg);
      console.log('✅ Saved updated blocklist to Firebase');
    } catch (e) {
      console.error('❌ Failed saving blocklist to Firebase:', e);
    }

    if (prospectId) {
      setScanResults(prev => prev.filter(p => p.id !== prospectId));
      setAllProspects(prev => prev.filter(p => p.id !== prospectId));
    }

    console.log('=== BLOCKLIST ADDITION COMPLETE ===');
  };

  const removeFromBlocklist = async (stableId: string) => {
    console.log('Removing from universal blocklist (ID):', stableId);
    const newBlocklist = blocklist.filter(id => id !== stableId);
    setBlocklist(newBlocklist);
    try {
      await saveUniversalBlocklist(newBlocklist, selectedOrg);
      console.log('✅ Saved updated blocklist to Firebase');
    } catch (e) {
      console.error('❌ Failed saving blocklist to Firebase:', e);
    }
  };

  const testBlocklistPersistence = () => {
    console.log('=== BLOCKLIST PERSISTENCE TEST ===');
    console.log('Current blocklist state:', blocklist);
    console.log('Raw localStorage:', localStorage.getItem('raedar_blocklist'));
    console.log('All localStorage keys:', Object.keys(localStorage));
    
    // Test: Try to reload the blocklist
    const savedBlocklist = localStorage.getItem('raedar_blocklist');
    if (savedBlocklist) {
      try {
        const parsed = JSON.parse(savedBlocklist);
        console.log('Successfully parsed saved blocklist:', parsed);
        alert(`Found ${parsed.length} blocked businesses in localStorage: ${parsed.join(', ')}`);
      } catch (error) {
        console.error('Failed to parse saved blocklist:', error);
        alert('Error parsing saved blocklist');
      }
    } else {
      console.log('No blocklist found in localStorage');
      alert('No blocklist found in localStorage');
    }
  };

  const checkBlocklistStatus = () => {
    console.log('=== BLOCKLIST STATUS CHECK ===');
    console.log('React state blocklist:', blocklist);
    console.log('localStorage blocklist:', localStorage.getItem('raedar_blocklist'));
    console.log('blocklistLoaded state:', blocklistLoaded);
  };

  // Resolve a friendly display name for a blocked stable ID (placeId or businessHash)
  const resolveBlockedDisplayName = (stableId: string): string => {
    // Try exact id match from currently loaded prospects
    const byId = allProspects.find(p => p.id === stableId) ||
                 allAvailableProspects.find(p => p.id === stableId) ||
                 scanResults.find(p => p.id === stableId);
    if (byId) return byId.name;

    // Try businessHash match from AI analyses
    const byHash = allProspects.find(p => p.aiAnalysis?.businessHash === stableId) ||
                   allAvailableProspects.find(p => p.aiAnalysis?.businessHash === stableId) ||
                   scanResults.find(p => p.aiAnalysis?.businessHash === stableId);
    if (byHash) return byHash.name;

    // Fallback to the raw ID
    return stableId;
  };

  // Current advertiser helper functions
  const markAsCurrentAdvertiser = async (prospect: Prospect) => {
    try {
      // Generate business hash for consistent identification
      const businessHash = aiService.generateBusinessHash({
        name: prospect.name,
        category: prospect.category,
        address: prospect.address,
        distance: 0,
        types: [prospect.category],
        businessStatus: prospect.openNow ? 'Open' : 'Closed',
        website: prospect.website,
        phone: prospect.phone
      });

      await addCurrentAdvertiser(prospect.name, prospect.address, businessHash, selectedOrg);
      
      // Update local state
      setCurrentAdvertisers(prev => ({
        ...prev,
        [businessHash]: {
          businessName: prospect.name,
          markedByName: currentUser?.name || currentUser?.email || 'Unknown',
          markedAt: new Date()
        }
      }));
      
      alert('✅ Business marked as current advertiser');
    } catch (error: any) {
      console.error('Error marking as current advertiser:', error);
      alert('❌ Error: ' + error.message);
    }
  };

  const unmarkCurrentAdvertiser = async (prospect: Prospect) => {
    try {
      // Generate business hash
      const businessHash = aiService.generateBusinessHash({
        name: prospect.name,
        category: prospect.category,
        address: prospect.address,
        distance: 0,
        types: [prospect.category],
        businessStatus: prospect.openNow ? 'Open' : 'Closed',
        website: prospect.website,
        phone: prospect.phone
      });

      await removeCurrentAdvertiser(businessHash, selectedOrg);
      
      // Update local state
      setCurrentAdvertisers(prev => {
        const newState = { ...prev };
        delete newState[businessHash];
        return newState;
      });
      
      alert('✅ Business unmarked as current advertiser');
    } catch (error: any) {
      console.error('Error unmarking current advertiser:', error);
      alert('❌ Error: ' + error.message);
    }
  };



  const clearAllBlocklist = async () => {
    if (!window.confirm(`Are you sure you want to clear ALL ${blocklist.length} blocked businesses? This action cannot be undone.`)) {
      return;
    }
    
    try {
      console.log('🗑️ Clearing all blocklist data...');
      setBlocklist([]);
      await saveUniversalBlocklist([], selectedOrg);
      console.log('✅ Cleared blocklist in Firebase');
    } catch (error: any) {
      console.error('❌ Error clearing blocklist:', error);
      alert('❌ Error clearing blocklist: ' + error.message);
    }
  };

  const addTestToBlocklist = () => {
    const testBusiness = `Test Business ${Date.now()}`;
    console.log('Adding test business to blocklist:', testBusiness);
    addToBlocklist(testBusiness);
    alert(`Added "${testBusiness}" to blocklist. Check console for details.`);
  };

  const exportProspectsToCSV = () => {
    const csvContent = [
      ['Name', 'Category', 'Address', 'Phone', 'Website', 'Score', 'Rating', 'Reviews'],
      ...scanResults.map(prospect => [
        prospect.name,
        prospect.category,
        prospect.address,
        prospect.phone,
        prospect.website,
        ((prospect.score || 0) * 100).toFixed(0) + '%',
        prospect.rating || 'N/A',
        prospect.reviews || 0
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportAllProspectsToCSV = () => {
    if (allProspects.length === 0) {
      alert('No prospects to export');
      return;
    }

    const csvContent = [
      ['Name', 'Category', 'Address', 'Rating', 'Score'],
      ...allProspects.map(prospect => [
        prospect.name,
        prospect.category,
        prospect.address,
        (prospect.rating ?? 'N/A'),
        ((prospect.score || 0) * 100).toFixed(0) + '%'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_prospects_summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportCallLogToCSV = () => {
    if (log.length === 0) {
      alert('No call history to export');
      return;
    }

    const csvContent = [
      ['Date', 'Time', 'Business Name', 'Category', 'Phone', 'Website', 'Address', 'Outcome', 'Notes'],
      ...log.map(entry => [
        formatMdy(new Date(entry.timestamp)),
        new Date(entry.timestamp).toLocaleTimeString(),
        entry.name,
        entry.category,
        entry.phone || '',
        entry.website || '',
        entry.address,
        entry.outcome,
        entry.note || ''
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call_log_${currentUser?.name || 'user'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearCallLog = () => {
    if (log.length === 0) {
      alert('No call history to clear');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to clear all ${log.length} call log entries? This action cannot be undone.`);
    if (confirmed) {
      setLog([]);
      if (currentUser) {
        clearUserCallLogs(currentUser.id).catch(() => console.warn('Failed to clear call logs in Firebase'));
      }
      alert('Call history cleared successfully');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!isSuperAdmin()) return;
    
    try {
      // Delete from Firebase
      await deleteFirebaseUser(userId);
      
      // Update local state
      const updatedUsers = allUsers.filter(user => user.id !== userId);
      setAllUsers(updatedUsers);
      
      console.log('User deleted successfully:', userId);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const updateUserRole = async (userId: string, newRole: 'user' | 'admin' | 'SuperAdmin') => {
    if (!isSuperAdmin()) return;
    
    try {
      // Update in Firebase
      await updateFirebaseUserRole(userId, newRole);
      
      // Update local state
      const updatedUsers = allUsers.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      );
      setAllUsers(updatedUsers);
      
      console.log('User role updated successfully:', userId, 'to', newRole);
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role. Please try again.');
    }
  };

  // Manually refresh users list from Firestore
  const refreshUsers = async () => {
    if (!isAdmin()) return;
    try {
      const firebaseUsers = await getAllUsers();
      setAllUsers(firebaseUsers);
      localStorage.setItem('raedar_users', JSON.stringify(firebaseUsers));
      console.log('🔄 Users refreshed:', firebaseUsers.length);
    } catch (error) {
      console.error('Failed to refresh users:', error);
      alert('Failed to refresh users. Please try again.');
    }
  };

  // Cache management
  const getCacheStats = () => {
    try {
      // Get cache stats from the existing aiService instance
      const cacheSize = aiService.getCacheSize();
      return { 
        total: cacheSize,
        valid: cacheSize, // For now, assume all entries are valid
        expired: 0 
      };
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
    }
    return { total: 0, valid: 0, expired: 0 };
  };

  const clearAICache = async () => {
    if (!isAdmin()) return;
    
    try {
      // Clear from Firebase
      await clearUniversalAICache();
      
      // Clear from localStorage as fallback
      localStorage.removeItem('raedar_ai_cache');
      
      // Clear from in-memory cache
      aiService.clearCache();
      
      setCacheStats({ total: 0, valid: 0, expired: 0 });
      alert('AI cache cleared successfully');
    } catch (error) {
      console.error('Error clearing AI cache:', error);
      // Fallback to localStorage only
      localStorage.removeItem('raedar_ai_cache');
      aiService.clearCache();
      setCacheStats({ total: 0, valid: 0, expired: 0 });
      alert('AI cache cleared (local only)');
    }
  };

  // Add invitation management functions
  const handleGenerateInvitation = async () => {
    try {
      const invitationCode = await generateInvitation(
        currentUser?.name || currentUser?.email || 'Unknown',
        '' // Optional email for specific user
      );
      
      // Copy invitation code to clipboard
      try {
        await navigator.clipboard.writeText(invitationCode);
        alert(`✅ Invitation code generated and copied to clipboard:\n\n${invitationCode}\n\nShare this code with the new user.`);
      } catch (clipboardError) {
        // Fallback if clipboard API fails
        alert(`✅ Invitation code generated: ${invitationCode}\n\nShare this code with the new user.\n\n(Code was not copied to clipboard - please copy manually)`);
      }
    } catch (error: any) {
      alert('❌ Error generating invitation: ' + error.message);
    }
  };

  // Load and persist user-specific category selection
  useEffect(() => {
    if (!currentUser) return;
    const key = `raedar_categories_${currentUser.id}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.every((c) => typeof c === 'string')) {
          const valid = parsed
            .filter((c): c is CategoryGroup => (CATEGORY_GROUPS as readonly string[]).includes(c))
            .slice(0, MAX_PRIMARY_SCAN_CATEGORIES);
          setSelectedCategories(valid);
        }
      } catch {}
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const key = `raedar_categories_${currentUser.id}`;
    localStorage.setItem(key, JSON.stringify(selectedCategories));
  }, [selectedCategories, currentUser]);

  // Show login if no user
  if (!currentUser) {
    return <Login onLogin={setCurrentUser} />;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent} className="header-content">
          <div style={styles.brandSection} className="brand-section">
            <Logo size={96} color="white" />
            <p style={styles.brandSubtitle} className="brand-subtitle">AI-POWERED PROSPECTING CRM</p>
          </div>
          <div style={styles.userInfo} className="user-info">
            <div style={styles.userDetails} className="user-details">
              <span style={styles.userName}>{currentUser.name}</span>
              <span style={styles.userRole}>{currentUser.role}</span>
            </div>
            <button 
              style={{
                ...styles.logoutButton,
                backgroundColor: 'rgba(76, 175, 80, 0.15)',
                marginRight: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(76, 175, 80, 0.25)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(76, 175, 80, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onClick={() => {
                // Reset search state (but don't reset instruction preference)
                setScanResults([]);
                setAllProspects([]);
                setActiveTab('prospects');
                // Don't reset showInstructions - let Firebase preference control it
                setProspectsLoaded(0);
                setHasMoreProspects(false);
                setAllAvailableProspects([]);
                setAppointmentStrategy(null);
                setShowAppointmentModal(false);
                setIsGeneratingStrategy(false);
                // Scroll to top
                window.scrollTo(0, 0);
              }}
            >
              New Search
            </button>
            <button 
              style={{
                ...styles.logoutButton,
                backgroundColor: 'rgba(33, 150, 243, 0.15)',
                marginRight: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(33, 150, 243, 0.25)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(33, 150, 243, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onClick={async () => {
                setShowInstructions(true);
                setDontShowAgain(false);
                // Clear the preference in Firebase and localStorage
                if (currentUser) {
                  try {
                    await updateUserPreferences(currentUser.id, { hideInstructions: false });
                    localStorage.removeItem('raedar_hide_instructions');
                  } catch (error) {
                    console.error('Error clearing instruction preference:', error);
                    localStorage.removeItem('raedar_hide_instructions');
                  }
                }
              }}
            >
              Help
            </button>
            <button 
              style={styles.logoutButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onClick={async () => {
                try {
                  // Clear all user data before logout
                  clearUserData();
                  // Sign out from Firebase
                  await signOutUser();
                  console.log('👋 User logged out from Firebase, all data cleared');
                } catch (error) {
                  console.error('Error signing out:', error);
                  // Fallback: just clear local state
                  setCurrentUser(null);
                }
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Instructions Window */}
      {showInstructions && (
        <div style={styles.instructionsOverlay}>
          <div style={styles.instructionsWindow}>
            <div style={styles.instructionsHeader}>
              <h2 style={styles.instructionsTitle}>Welcome to rAEdar!</h2>
              <button 
                style={styles.closeInstructionsButton}
                onClick={closeInstructions}
              >
                ✕
              </button>
            </div>
            <div style={styles.instructionsContent}>
              <div style={styles.instructionStep}>
                <span style={styles.stepNumber}>1</span>
                <div style={styles.stepContent}>
                  <h3 style={styles.stepTitle}>Set Your Location</h3>
                  <p style={styles.stepDescription}>
                    Use the <strong>"Use GPS"</strong> button to automatically detect your current location, 
                    or <strong>"Set Address"</strong> to manually enter an address.
                  </p>
                </div>
              </div>
              
              <div style={styles.instructionStep}>
                <span style={styles.stepNumber}>2</span>
                <div style={styles.stepContent}>
                  <h3 style={styles.stepTitle}>Adjust Search Radius</h3>
                  <p style={styles.stepDescription}>
                    Use the <strong>"Radius"</strong> slider to set how far from your location to search (0.5–1.5 miles).
                  </p>
                </div>
              </div>
              
              <div style={styles.instructionStep}>
                <span style={styles.stepNumber}>3</span>
                <div style={styles.stepContent}>
                  <h3 style={styles.stepTitle}>Choose Prospect Count</h3>
                  <p style={styles.stepDescription}>
                    Use the <strong>"Prospects"</strong> slider to select how many business prospects to return (5-50).
                  </p>
                </div>
              </div>
              
              <div style={styles.instructionStep}>
                <span style={styles.stepNumber}>4</span>
                <div style={styles.stepContent}>
                  <h3 style={styles.stepTitle}>Start Your Search</h3>
                  <p style={styles.stepDescription}>
                    Click the <strong>"Scan Area"</strong> button to find business prospects in your selected area.
                  </p>
                </div>
              </div>
              
              <div style={styles.instructionNote}>
                <p style={styles.noteText}>
                  💡 <strong>Tip:</strong> The map will show your search area and the businesses found. 
                  You can also double-click anywhere on the map to set a new center point.
                </p>
              </div>
              
              <div style={styles.dontShowAgainContainer}>
                <label style={styles.dontShowAgainLabel}>
                  <input
                    type="checkbox"
                    checked={dontShowAgain}
                    onChange={(e) => {
                      console.log('Checkbox changed to:', e.target.checked);
                      setDontShowAgain(e.target.checked);
                    }}
                    style={styles.dontShowAgainCheckbox}
                  />
                  <span style={styles.dontShowAgainText}>Don't show this again</span>
                </label>
              </div>
              
              <div style={styles.instructionsFooter}>
                <button 
                  style={styles.getStartedButton}
                  onClick={closeInstructions}
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={styles.controls}>
        {/* Location Controls */}
        <div style={styles.locationControls}>
          <div style={styles.locationButtons}>
            <button 
              style={styles.locationButton}
              onClick={useGPSLocation}
            >
              📍 Use GPS
            </button>
            <button 
              style={styles.locationButton}
              onClick={setLocationFromAddress}
              disabled={!addressInput.trim()}
            >
              🏠 Set Address
            </button>
          </div>
          
          <div style={styles.addressInput}>
            <input
              type="text"
              placeholder="Enter address (e.g., 123 Main St, Birmingham, AL)"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              style={styles.addressInputField}
              onKeyPress={(e) => e.key === 'Enter' && setLocationFromAddress()}
            />
          </div>
          
        <div style={styles.currentLocation}>
            <span style={styles.locationLabel}>📍 Current Location:</span>
            <span style={styles.locationText}>{currentLocation}</span>
          </div>
        </div>

      {/* Quick Business Search (bypasses categories & radius) */}
      <div style={styles.controlRow}>
        <label style={styles.label}>Quick search</label>
        <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
          <select
            value={quickMode}
            onChange={(e) => setQuickMode(e.target.value as any)}
            style={{ ...styles.outcomeSelect, minWidth: '140px' }}
          >
            <option value="business">Business</option>
            <option value="category">Category</option>
          </select>
          <input
            type="text"
            placeholder={quickMode === 'business' ? 'Business name (optionally, city)' : 'Category (e.g., restaurant, dental, hvac)'}
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            style={{ ...styles.addressInputField, flex: 1 }}
          />
          {/* Optional category dropdown removed to avoid confusion */}
          <button
            style={{
              backgroundColor: '#e5e7eb',
              color: '#111827',
              border: '1px solid #d1d5db',
              padding: '10px 14px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={async () => {
              const q = quickSearch.trim();
              if (!q) return;
              try {
                if (quickMode === 'business') {
                  const found = await lookupBusinessByName(q, userPos.lat, userPos.lng, radius);
                  if (!found) {
                    alert('No matching business found within your radius. Try a more specific name.');
                    return;
                  }
                  let resolvedCategory = found.category;
                  if (!resolvedCategory || resolvedCategory === 'Business') {
                    const input = window.prompt('Enter the business category (e.g., Restaurant, Dental, HVAC):', '');
                    if (input && input.trim()) {
                      resolvedCategory = input.trim();
                    }
                  }
                  const prospect: Prospect = {
                    id: found.placeId || found.id,
                    name: found.name,
                    category: resolvedCategory || 'Business',
                    categoryGroup: found.categoryGroup || inferCategoryGroupFromText(resolvedCategory || found.category),
                    address: found.address || '',
                    city: found.city || '',
                    state: found.state || '',
                    zip: found.zip || '',
                    phone: found.phone || '',
                    website: found.website || '',
                    lat: found.lat,
                    lng: found.lng,
                    rating: found.rating,
                    reviews: found.reviews || 0,
                    openNow: found.openNow,
                    placeId: found.placeId
                  };
                  setScanResults([prospect]);
                  setAllAvailableProspects([prospect]);
                  setProspectsLoaded(1);
                  setHasMoreProspects(false);
                  setActiveTab('prospects');
                } else {
                  // category mode: run a constrained scan by keyword within radius, limited to prospectCount
                  const results = await lookupCategoryWithinRadius(q, userPos.lat, userPos.lng, radius, prospectCount);
                  if (!results || results.length === 0) {
                    alert('No results found for that category within your radius.');
                    return;
                  }
                  const prospects: Prospect[] = results.map((b, index) => ({
                    id: b.placeId || b.id || `prospect_${index}`,
                    name: b.name,
                    category: b.category || q,
                    categoryGroup: b.categoryGroup || inferCategoryGroupFromText(b.category || q),
                    address: b.address || '',
                    city: b.city || '',
                    state: b.state || '',
                    zip: b.zip || '',
                    phone: b.phone || '',
                    website: b.website || '',
                    lat: b.lat,
                    lng: b.lng,
                    rating: b.rating,
                    reviews: b.reviews || 0,
                    openNow: b.openNow || true,
                    placeId: b.placeId,
                    score: 0
                  }));
                  setScanResults(prospects);
                  setAllAvailableProspects(prospects);
                  setProspectsLoaded(prospects.length);
                  setHasMoreProspects(false);
                  setActiveTab('prospects');
                }
              } catch (e) {
                alert('Quick search failed. Check console for details.');
                console.error(e);
              }
            }}
          >
            Find
          </button>
        </div>
      </div>

        <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Organization</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            {(Object.keys(ORGANIZATION_LABELS) as OrganizationKey[]).map((orgKey) => (
              <button
                key={orgKey}
                style={{
                  ...styles.quickActionButton,
                  background: selectedOrg === orgKey ? '#dbeafe' : '#fff',
                  color: selectedOrg === orgKey ? '#1d4ed8' : '#374151',
                  border: `1px solid ${selectedOrg === orgKey ? '#93c5fd' : '#d1d5db'}`
                }}
                onClick={() => handleOrganizationChange(orgKey)}
              >
                {ORGANIZATION_LABELS[orgKey]}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Scoring label: {ORGANIZATION_SCORE_LABELS[selectedOrg]}
            {isGemmetricOrg ? ' • Lite Scan runs only when you click Run Lite Scan' : ''}
          </div>
        </div>

        <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Scan Type</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <button
              style={{
                ...styles.quickActionButton,
                background: scanMode === 'local' ? '#dbeafe' : '#fff',
                color: scanMode === 'local' ? '#1d4ed8' : '#374151',
                border: `1px solid ${scanMode === 'local' ? '#93c5fd' : '#d1d5db'}`
              }}
              onClick={() => setScanMode('local')}
            >
              Local Multi-Industry
            </button>
            <button
              style={{
                ...styles.quickActionButton,
                background: scanMode === 'vertical' ? '#dbeafe' : '#fff',
                color: scanMode === 'vertical' ? '#1d4ed8' : '#374151',
                border: `1px solid ${scanMode === 'vertical' ? '#93c5fd' : '#d1d5db'}`
              }}
              onClick={() => setScanMode('vertical')}
            >
              Vertical Single-Industry
            </button>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Projected search radius: {projectedScanRadius.toFixed(scanMode === 'vertical' ? 0 : 1)} miles
          </div>
        </div>

        {scanMode === 'local' ? (
          <>
            <div style={styles.controlRow}>
              <label style={styles.label}>Radius: {radius.toFixed(1)} miles</label>
              <input
                type="range"
                min="0.5"
                max="1.5"
                step="0.1"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                style={styles.slider}
              />
            </div>
            
            <div style={styles.controlRow}>
              <label style={styles.label}>Prospects: {prospectCount}</label>
              <input
                type="range"
                min="5"
                max="50"
                value={prospectCount}
                onChange={(e) => setProspectCount(Number(e.target.value))}
                style={styles.slider}
              />
            </div>
            
            <div style={styles.controlRow}>
              <label style={styles.label}>Industries</label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Select up to {MAX_PRIMARY_SCAN_CATEGORIES} industries. {selectedCategories.length} of {MAX_PRIMARY_SCAN_CATEGORIES} selected.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {CATEGORY_GROUPS.map((group) => {
                  const checked = selectedCategories.includes(group);
                  const disabled = !checked && selectedCategories.length >= MAX_PRIMARY_SCAN_CATEGORIES;
                  return (
                    <label
                      key={group}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        opacity: disabled ? 0.55 : 1
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => togglePrimaryCategory(group, e.target.checked)}
                      />
                      {CATEGORY_GROUP_LABELS[group]}
                    </label>
                  );
                })}
              </div>
            </div>

            <button 
              style={{
                ...styles.scanButton,
                ...((isScanning || selectedCategories.length === 0) && {
                  backgroundColor: '#059669',
                  cursor: 'not-allowed',
                  position: 'relative' as const,
                  opacity: selectedCategories.length === 0 ? 0.8 : 1
                })
              }}
              onClick={handleScan}
              disabled={isScanning || selectedCategories.length === 0}
            >
              {isScanning ? (
                <>
                  <span style={{ animation: 'pulse 1s ease-in-out infinite' }}>🔍</span>
                  {" Scanning..."}
                </>
              ) : (
                "🔍 Scan Area"
              )}
            </button>
          </>
        ) : (
          <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Vertical Scan</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Single-industry scan lane for broader vertical targeting.
            </div>

            <div style={styles.controlRow}>
              <label style={styles.label}>Industry</label>
              <select
                value={verticalCategory}
                onChange={(e) => setVerticalCategory(e.target.value as CategoryGroup)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}
              >
                {CATEGORY_GROUPS.map((group) => (
                  <option key={group} value={group}>
                    {CATEGORY_GROUP_LABELS[group]}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.controlRow}>
              <label style={styles.label}>Vertical Radius: {verticalRadius.toFixed(0)} miles</label>
              {isGemmetricOrg ? (
                <select
                  value={verticalRadius}
                  onChange={(e) => setVerticalRadius(Number(e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}
                >
                  {GEMMETRIC_AGENCY_RADIUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option} miles</option>
                  ))}
                </select>
              ) : (
                <input
                  type="range"
                  min="5"
                  max="15"
                  step="1"
                  value={verticalRadius}
                  onChange={(e) => setVerticalRadius(Number(e.target.value))}
                  style={styles.slider}
                />
              )}
            </div>

            <button
              style={{
                ...styles.quickActionButton,
                width: '100%',
                justifyContent: 'center',
                opacity: isScanning ? 0.8 : 1,
                cursor: isScanning ? 'not-allowed' : 'pointer'
              }}
              onClick={handleVerticalScan}
              disabled={isScanning}
            >
              🎯 Scan Vertical
            </button>
          </div>
        )}

        {scanHistory.length > 0 && (
          <div style={{
            marginTop: "12px",
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "10px",
            background: "#f8fafc"
          }}>
            {(() => {
              const active = scanHistory.find((s) => s.id === activeScanId) || scanHistory[0];
              return (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "13px", color: "#111827" }}>
                    <strong>Last Scan:</strong> {active.locationLabel} • {active.prospectCount} prospects • {formatMdyTime(new Date(active.createdAt))}
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: 2 }}>
                      {formatScanCategories(active.categories)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      style={{ ...styles.quickActionButton, padding: "6px 10px" }}
                      onClick={() => {
                        applyScanPayload(active.prospects, active.id);
                        setActiveTab("prospects");
                      }}
                    >
                      Resume Last Scan
                    </button>
                    <button
                      style={{ ...styles.quickActionButton, padding: "6px 10px", background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' }}
                      onClick={() => void removeScanHistoryEntry(active.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })()}

            <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {scanHistory.slice(0, SCAN_HISTORY_UI_LIMIT).map((scan) => (
                <div
                  key={scan.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "6px 8px",
                    background: scan.id === activeScanId ? "#eff6ff" : "#ffffff"
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#374151" }}>
                    <div>{scan.locationLabel} • {scan.prospectCount} • {formatMdyTime(new Date(scan.createdAt))}</div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: 2 }}>
                      {formatScanCategories(scan.categories)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      style={{ ...styles.quickActionButton, padding: "4px 8px", fontSize: "12px" }}
                      onClick={() => {
                        applyScanPayload(scan.prospects, scan.id);
                        setActiveTab("prospects");
                      }}
                    >
                      Open
                    </button>
                    <button
                      style={{ ...styles.quickActionButton, padding: "4px 8px", fontSize: "12px", background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' }}
                      onClick={() => void removeScanHistoryEntry(scan.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#111827" }}>
                <input
                  type="checkbox"
                  checked={showScanCoverage}
                  onChange={(e) => setShowScanCoverage(e.target.checked)}
                />
                Show scan coverage
              </label>
              <select
                value={scanCoverageDays}
                onChange={(e) => setScanCoverageDays(Number(e.target.value) as 30 | 90 | 180)}
                style={{ ...styles.outcomeSelect, padding: "6px 8px", fontSize: "12px", maxWidth: 140 }}
              >
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 180 days</option>
              </select>
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                Hot = recent scans, cooler = older scans
              </span>
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                Click a coverage ring to see the scanned industries.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Map */}
      <div style={styles.mapContainer}>
        <GoogleMap
          center={userPos}
          prospects={scanResults}
          radius={projectedScanRadius}
          scanCoverageEntries={mapScanCoverageEntries}
          coverageWindowDays={scanCoverageDays}
          onLocationChange={handleMapClick}
          selectedProspect={null}
          setSelectedProspect={() => {}}
        />
        <ScanningAnimation isScanning={isScanning} />
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        {/* User Tabs - Row 1 */}
        <div style={styles.tabRow}>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === 'prospects' && styles.activeTabButton)
            }}
            onClick={() => setActiveTab('prospects')}
          >
            📊 Prospects ({scanResults.length})
          </button>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === 'saved' && styles.activeTabButton)
            }}
            onClick={() => setActiveTab('saved')}
          >
            💾 Saved ({savedProspects.length})
          </button>
          <button 
            style={{
              ...styles.tabButton,
              ...((activeTab === 'activity' || activeTab === 'call-sheet' || activeTab === 'calendar') && styles.activeTabButton)
            }}
            onClick={() => setActiveTab('activity')}
          >
            📋 Activity
          </button>
        </div>
        
        {/* Admin Tabs - Row 2 (only visible to admins) */}
        {isAdmin() && (
          <div style={styles.tabRow}>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'all-prospects' && styles.activeTabButton)
              }}
              onClick={() => setActiveTab('all-prospects')}
            >
              📊 All Prospects ({allProspects.length})
            </button>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'blocked' && styles.activeTabButton)
              }}
              onClick={() => setActiveTab('blocked')}
            >
              🚫 Blocked ({blocklist.length})
            </button>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'current-advertisers' && styles.activeTabButton)
              }}
              onClick={() => setActiveTab('current-advertisers')}
            >
              ✅ Current Advertisers ({Object.keys(currentAdvertisers).length})
            </button>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'admin' && styles.activeTabButton)
              }}
              onClick={() => setActiveTab('admin')}
            >
              ⚙️ Admin Panel
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'prospects' && (
          <div style={styles.prospectsList}>
            {scanResults.map(prospect => (
              <div key={prospect.id} style={styles.prospectCard}>
                                <div style={styles.prospectHeader}>
                  <div style={styles.prospectTitleRow}>
                    <h3 style={styles.prospectName}>{prospect.name}</h3>
                    <div style={styles.headerRight}>
                      {isProspectInCurrentWeekSheet(prospect) ? (
                        <span style={styles.callSheetBadge}>Added this week</span>
                      ) : null}
                      <span style={styles.prospectScore} title={ORGANIZATION_SCORE_LABELS[selectedOrg]}>
                        {formatProspectScore(prospect)}
                      </span>
                      {isAdmin() && (
                        <button
                          style={styles.blockButton}
                          onClick={() => addToBlocklist(prospect.name, prospect.id)}
                        >
                          Block
                        </button>
                      )}
                      {/* Current Advertiser Button - Spectrum Reach only */}
                      {!isGemmetricOrg && (() => {
                        const businessHash = aiService.generateBusinessHash({
                          name: prospect.name,
                          category: prospect.category,
                          address: prospect.address,
                          distance: 0,
                          types: [prospect.category],
                          businessStatus: prospect.openNow ? 'Open' : 'Closed',
                          website: prospect.website,
                          phone: prospect.phone
                        });
                        const isMarked = !!currentAdvertisers[businessHash];

                        if (!isMarked) {
                          return (
                            <button
                              style={styles.markAdvertiserButton}
                              onClick={() => markAsCurrentAdvertiser(prospect)}
                              title="Mark as current Spectrum Reach advertiser"
                            >
                              ✅ Current Advertiser
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                <p style={styles.prospectCategory}>{prospect.category}</p>
                <p style={styles.prospectAddress}>{prospect.address}</p>
                
                {/* Current Advertiser Status */}
                {!isGemmetricOrg && (() => {
                  const businessHash = aiService.generateBusinessHash({
                    name: prospect.name,
                    category: prospect.category,
                    address: prospect.address,
                    distance: 0,
                    types: [prospect.category],
                    businessStatus: prospect.openNow ? 'Open' : 'Closed',
                    website: prospect.website,
                    phone: prospect.phone
                  });
                  const advertiserInfo = currentAdvertisers[businessHash];
                  
                  if (advertiserInfo) {
                    return (
                      <div style={styles.currentAdvertiserContainer}>
                        <div style={styles.currentAdvertiserHeader}>
                          <span style={styles.currentAdvertiserIcon}>✅</span>
                          <span style={styles.currentAdvertiserTitle}>Current Spectrum Reach Advertiser</span>
                        </div>
                        <p style={styles.currentAdvertiserInfo}>
                          Marked by <strong>{advertiserInfo.markedByName}</strong> on{' '}
                          {advertiserDateText(advertiserInfo.markedAt)}
                        </p>
                        {isAdmin() && (
                          <button
                            style={styles.unmarkAdvertiserButton}
                            onClick={() => unmarkCurrentAdvertiser(prospect)}
                            title="Remove current advertiser status (Admin only)"
                          >
                            🗑️ Unmark
                          </button>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {renderProspectAiInsights(prospect)}
                {renderGemmetricLiteScanPrompt(prospect)}
                {renderLiteScanInsights(prospect)}
                
                {/* Quick Action Buttons - moved below address */}
                <div style={styles.quickActions}>
                  <button
                    style={styles.quickActionButton}
                    onClick={async () => {
                      let targetPhone = prospect.phone;
                      if (!targetPhone) {
                        try {
                          const details = await fetchProspectDetailsOnDemand(prospect);
                          targetPhone = details.phone || "";
                          if (targetPhone) {
                            const updated = { ...prospect, ...details, phone: targetPhone };
                            setScanResults(prev => prev.map(p => p.id === prospect.id ? updated : p));
                            setAllAvailableProspects(prev => prev.map(p => p.id === prospect.id ? updated : p));
                            setAllProspects(prev => prev.map(p => p.id === prospect.id ? updated : p));
                          }
                        } catch (e) {
                          console.warn('Call details fetch failed', e);
                        }
                      }
                      if (targetPhone) window.open(`tel:${targetPhone}`, '_self');
                      else alert('Phone number not available.');
                    }}
                  >
                    📞 Call
                  </button>
                  <button
                    style={styles.quickActionButton}
                    onClick={() => {
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(prospect.address)}`;
                      window.open(url, '_blank');
                    }}
                  >
                    🗺️ Directions
                  </button>
                  <button
                    style={styles.quickActionButton}
                    onClick={async () => {
                      let targetWebsite = prospect.website;
                      if (!targetWebsite) {
                        try {
                          const details = await fetchProspectDetailsOnDemand(prospect);
                          targetWebsite = details.website || '';
                          if (targetWebsite) {
                            const updated = { ...prospect, ...details, website: targetWebsite };
                            setScanResults(prev => prev.map(p => p.id === prospect.id ? updated : p));
                            setAllAvailableProspects(prev => prev.map(p => p.id === prospect.id ? updated : p));
                            setAllProspects(prev => prev.map(p => p.id === prospect.id ? updated : p));
                          }
                        } catch (e) {
                          console.warn('Website details fetch failed', e);
                        }
                      }
                      if (targetWebsite) window.open(targetWebsite, '_blank');
                      else alert('Website not available.');
                    }}
                  >
                    🌐 Website
                  </button>
                  <button
                    style={{
                      ...styles.appointmentStrategyButton,
                      opacity: prospect.category && prospect.category !== 'Business' ? 1 : 0.5,
                      cursor: prospect.category && prospect.category !== 'Business' ? 'pointer' : 'not-allowed'
                    }}
                    onClick={() => {
                      if (!prospect.category || prospect.category === 'Business') {
                        alert('Set a category first to generate a high-quality strategy.');
                        return;
                        }
                      generateAppointmentStrategy(prospect);
                    }}
                    title="Get AI-powered appointment strategy"
                  >
                    🎯 Strategy
                  </button>
                  {!prospect.category || prospect.category === 'Business' ? (
                    <button
                      style={{ ...styles.quickActionButton, backgroundColor: '#fee2e2', color: '#991b1b' }}
                      onClick={() => {
                        const input = window.prompt('Enter the business category (e.g., Restaurant, Dental, HVAC):', '');
                        if (!input || !input.trim()) return;
                        const updated = { ...prospect, category: input.trim() };
                        setScanResults(prev => prev.map(p => p.id === prospect.id ? updated : p));
                        setAllAvailableProspects(prev => prev.map(p => p.id === prospect.id ? updated : p));
                      }}
                    >
                      Set category
                    </button>
                  ) : null}
                </div>
                
                <div style={styles.prospectActions}>
                  <button
                    style={styles.actionButton}
                    onClick={() => void logCall(prospect, "Saved for Later", "")}
                  >
                    💾 Save for Later
                  </button>
                  <button
                    style={{
                      ...styles.actionButton,
                      ...(isProspectInCurrentWeekSheet(prospect) ? styles.actionButtonDisabled : {})
                    }}
                    onClick={() => void addProspectCardToTodaySheet(prospect, false)}
                    disabled={isProspectInCurrentWeekSheet(prospect) || !!addingToWeekByProspectId[getProspectStableId(prospect).id]}
                  >
                    {addingToWeekByProspectId[getProspectStableId(prospect).id]
                      ? 'Adding...'
                      : isProspectInCurrentWeekSheet(prospect)
                        ? 'Added to This Week'
                        : '📋 Add to This Week'}
                  </button>
                </div>
              </div>
            ))}
            
            {/* Load More Prospects Button */}
            {hasMoreProspects && scanResults.length > 0 && (
              <div style={styles.loadMoreContainer}>
                <button 
                  style={styles.loadMoreButton}
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? '⏳ Loading...' : `📄 Load Next ${Math.min(prospectCount, 100 - prospectsLoaded)} Prospects`}
                </button>
                <p style={styles.loadMoreInfo}>
                  Showing {scanResults.length} of {Math.min(100, allAvailableProspects.length)} prospects 
                  ({prospectsLoaded}/{Math.min(100, allAvailableProspects.length)} loaded)
                </p>
              </div>
            )}
          </div>
        )}

        {(activeTab === 'activity' || activeTab === 'call-sheet' || activeTab === 'calendar') && currentUser && (
          <div style={styles.logList}>
            <div style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong>Due reminders</strong>
                <button style={styles.logActionButton} onClick={() => void refreshDueReminders()}>
                  Refresh
                </button>
              </div>
              {isLoadingDueReminders ? (
                <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>Loading reminders...</div>
              ) : dueReminders.length === 0 ? (
                <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>No due reminders right now.</div>
              ) : (
                <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                  {dueReminders.slice(0, 8).map((r) => {
                    const dueDate = new Date(r.nextReminderAt);
                    const startToday = new Date();
                    startToday.setHours(0, 0, 0, 0);
                    const status = dueDate < startToday ? 'Overdue' : 'Due today';
                    return (
                      <div key={r.id} style={{ border: '1px solid #f3f4f6', borderRadius: 8, padding: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {status} • {formatMdyTime(new Date(r.nextReminderAt))}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {r.phone ? (
                            <button style={styles.logActionButton} onClick={() => window.open(`tel:${r.phone}`, '_self')}>
                              Call
                            </button>
                          ) : null}
                          <button
                            style={{ ...styles.logActionButton, backgroundColor: '#fff', color: '#991b1b', border: '1px solid #fecaca' }}
                            onClick={() => void clearDueReminder(r)}
                            disabled={clearingReminderId === r.id}
                          >
                            {clearingReminderId === r.id ? 'Removing...' : 'Remove reminder'}
                          </button>
                          <button style={styles.logActionButton} onClick={() => void addReminderToTodaySheet(r)}>
                            Add to This Week
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0 }}>Activity</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{
                    ...styles.logActionButton,
                    backgroundColor: (activeTab === 'call-sheet' || (activeTab === 'activity' && activityView === 'call-sheet')) ? '#e0f2fe' : '#fff',
                    color: (activeTab === 'call-sheet' || (activeTab === 'activity' && activityView === 'call-sheet')) ? '#0c4a6e' : '#1f2937',
                    border: `1px solid ${(activeTab === 'call-sheet' || (activeTab === 'activity' && activityView === 'call-sheet')) ? '#7dd3fc' : '#d1d5db'}`
                  }}
                  onClick={() => {
                    setActivityView('call-sheet');
                    setActiveTab('activity');
                  }}
                >
                  Call Sheet
                </button>
                <button
                  style={{
                    ...styles.logActionButton,
                    backgroundColor: (activeTab === 'calendar' || (activeTab === 'activity' && activityView === 'calendar')) ? '#e0f2fe' : '#fff',
                    color: (activeTab === 'calendar' || (activeTab === 'activity' && activityView === 'calendar')) ? '#0c4a6e' : '#1f2937',
                    border: `1px solid ${(activeTab === 'calendar' || (activeTab === 'activity' && activityView === 'calendar')) ? '#7dd3fc' : '#d1d5db'}`
                  }}
                  onClick={() => {
                    setActivityView('calendar');
                    setActiveTab('activity');
                  }}
                >
                  Calendar
                </button>
              </div>
            </div>
            {(activeTab === 'call-sheet' || (activeTab === 'activity' && activityView === 'call-sheet')) ? (
              <CallSheetView
                currentUser={currentUser}
                orgKey={selectedOrg}
                savedProspects={savedProspects}
                onRejectedRemoveSaved={removeFromSavedByAnyIds}
              />
            ) : (
              <AppointmentCalendarView currentUser={currentUser} orgKey={selectedOrg} />
            )}
          </div>
        )}

        {activeTab === 'saved' && (
          <div style={styles.prospectsList}>
            {currentUser && (
              <div style={{ ...styles.sectionHeader, marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>
                  Saved ({visibleSavedProspects.length})
                  {savedViewFilter === 'callbacks' ? ` • Callbacks (${filteredCallbackSavedProspects.length})` : ''}
                </h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    style={{
                      ...styles.logActionButton,
                      border: `1px solid ${savedViewFilter === 'all' ? '#93c5fd' : '#e5e7eb'}`,
                      background: savedViewFilter === 'all' ? '#eff6ff' : '#fff'
                    }}
                    onClick={() => setSavedViewFilter('all')}
                  >
                    All
                  </button>
                  <button
                    style={{
                      ...styles.logActionButton,
                      border: `1px solid ${savedViewFilter === 'callbacks' ? '#93c5fd' : '#e5e7eb'}`,
                      background: savedViewFilter === 'callbacks' ? '#eff6ff' : '#fff'
                    }}
                    onClick={() => setSavedViewFilter('callbacks')}
                  >
                    🔁 Callbacks ({filteredCallbackSavedProspects.length})
                  </button>
                  <select
                    value={savedCategoryFilter}
                    onChange={(e) => setSavedCategoryFilter(e.target.value as 'all' | CategoryGroup)}
                    style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }}
                  >
                    <option value="all">All industries</option>
                    {CATEGORY_GROUPS.map((group) => (
                      <option key={group} value={group}>
                        {CATEGORY_GROUP_LABELS[group]}
                      </option>
                    ))}
                  </select>
                  <button
                    style={styles.logActionButton}
                    disabled={isBuildingCallbackSheet || filteredCallbackDueSavedProspects.length === 0}
                    onClick={async () => {
                      if (!currentUser) return;
                      if (filteredCallbackDueSavedProspects.length === 0) {
                        alert('No due callbacks available right now.');
                        return;
                      }
                      setIsBuildingCallbackSheet(true);
                      try {
                        const dateKey = formatDateKey(new Date());
                        const name = `Callbacks ${formatMdy(new Date())}`;
                        const sheetId = await createCallSheet(currentUser.id, name, dateKey, selectedOrg);
                        for (const p of filteredCallbackDueSavedProspects) {
                          const prospectId = await ensureProspectV2(currentUser.id, p, true);
                          await addProspectToCallSheet(currentUser.id, sheetId, prospectId, selectedOrg);
                        }
                        setSelectedSavedIds({});
                        setActiveTab('activity');
                        setActivityView('call-sheet');
                        alert(`Created "${name}" with ${filteredCallbackDueSavedProspects.length} due callbacks.`);
                      } catch (e) {
                        console.error(e);
                        alert('Failed creating callback sheet.');
                      } finally {
                        setIsBuildingCallbackSheet(false);
                      }
                    }}
                  >
                    {isBuildingCallbackSheet ? 'Building callback sheet...' : `📋 Build callback sheet (${filteredCallbackDueSavedProspects.length})`}
                  </button>
                  <button
                    style={styles.logActionButton}
                    disabled={isAddingToCallSheet}
                    onClick={async () => {
                      if (!currentUser) return;
                      const ids = Object.entries(selectedSavedIds).filter(([, v]) => v).map(([k]) => k);
                      if (ids.length === 0) {
                        alert('Select one or more saved prospects first.');
                        return;
                      }
                      setIsAddingToCallSheet(true);
                      try {
                        const sheets = await listCallSheets(currentUser.id, selectedOrg);
                        const today = await getOrCreateTodayCallSheet(currentUser.id, selectedOrg);
                        const options = [today, ...sheets.filter(s => s.id !== today.id)];
                        const choice = window.prompt(
                          `Add ${ids.length} prospects to which call sheet?\n` +
                          options.map((s, i) => `${i + 1}) ${s.name} — ${s.dateKey}`).join('\n') +
                          `\n\nEnter a number (blank = This Week).`,
                          ''
                        );
                        const idx = Number(choice);
                        const selected = (Number.isFinite(idx) && idx > 0 && idx <= options.length) ? options[idx - 1] : today;

                        // Ensure v2 records exist and add to sheet
                        for (const legacyId of ids) {
                          const p = savedProspects.find(sp => sp.id === legacyId);
                          if (!p) continue;
                          const prospectId = await ensureProspectV2(currentUser.id, p, true);
                          await addProspectToCallSheet(currentUser.id, selected.id, prospectId, selectedOrg);
                        }
                        setSelectedSavedIds({});
                        setActiveTab('call-sheet');
                        alert(`Added ${ids.length} prospects to "${selected.name}"`);
                      } catch (e: any) {
                        console.error(e);
                        alert('Failed adding to call sheet. Check console for details.');
                      } finally {
                        setIsAddingToCallSheet(false);
                      }
                    }}
                  >
                    📋 Add selected to call sheet
                  </button>
                  <button
                    style={styles.logActionButton}
                    onClick={() => {
                      const allSelected = visibleSavedProspects.length > 0 && visibleSavedProspects.every(p => selectedSavedIds[p.id]);
                      if (allSelected) {
                        setSelectedSavedIds({});
                      } else {
                        const next: Record<string, boolean> = {};
                        visibleSavedProspects.forEach(p => { next[p.id] = true; });
                        setSelectedSavedIds(next);
                      }
                    }}
                  >
                    {visibleSavedProspects.length > 0 && visibleSavedProspects.every(p => selectedSavedIds[p.id]) ? 'Unselect all' : 'Select all'}
                  </button>
                </div>
              </div>
            )}
            {visibleSavedProspects.map(prospect => {
              const meta = getSavedProspectMeta(prospect);
              const callbackDue = !!meta?.callbackPending && (!meta?.nextReminderAt || new Date(meta.nextReminderAt).getTime() <= Date.now());
              const inCurrentWeekSheet = isProspectInCurrentWeekSheet(prospect);
              const stableProspectId = getProspectStableId(prospect).id;
              return (
              <div key={prospect.id} style={styles.prospectCard}>
                <div style={styles.prospectHeader}>
                  <div style={styles.prospectTitleRow}>
                    <h3 style={styles.prospectName}>{prospect.name}</h3>
                    <div style={styles.headerRight}>
                      {inCurrentWeekSheet ? (
                        <span style={styles.callSheetBadge}>Added this week</span>
                      ) : null}
                      {currentUser && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!selectedSavedIds[prospect.id]}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedSavedIds((prev) => ({ ...prev, [prospect.id]: checked }));
                            }}
                          />
                          Select
                        </label>
                      )}
                      {typeof prospect.score === 'number' && (
                        <span style={styles.prospectScore} title={ORGANIZATION_SCORE_LABELS[selectedOrg]}>
                          {formatProspectScore(prospect)}
                        </span>
                      )}
                      {isAdmin() && (
                        <button
                          style={styles.blockButton}
                          onClick={() => addToBlocklist(prospect.name, prospect.id)}
                        >
                          Block
                        </button>
                      )}
                      {!isGemmetricOrg && (() => {
                        const businessHash = aiService.generateBusinessHash({
                          name: prospect.name,
                          category: prospect.category,
                          address: prospect.address,
                          distance: 0,
                          types: [prospect.category],
                          businessStatus: prospect.openNow ? 'Open' : 'Closed',
                          website: prospect.website,
                          phone: prospect.phone
                        });
                        const isMarked = !!currentAdvertisers[businessHash];
                        if (!isMarked) {
                          return (
                            <button
                              style={styles.markAdvertiserButton}
                              onClick={() => markAsCurrentAdvertiser(prospect)}
                              title="Mark as current Spectrum Reach advertiser"
                            >
                              ✅ Current Advertiser
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                <p style={styles.prospectCategory}>{prospect.category}</p>
                {meta?.callbackPending ? (
                  <div style={{ marginTop: 4, fontSize: 12, color: callbackDue ? '#92400e' : '#374151' }}>
                    🔁 Callback {meta.nextReminderAt ? `• ${callbackDue ? 'Due' : 'Scheduled'} ${formatMdyTime(new Date(meta.nextReminderAt))}` : '• Due'}
                  </div>
                ) : null}
                <p style={styles.prospectAddress}>{prospect.address}</p>

                {!isGemmetricOrg && (() => {
                  const businessHash = aiService.generateBusinessHash({
                    name: prospect.name,
                    category: prospect.category,
                    address: prospect.address,
                    distance: 0,
                    types: [prospect.category],
                    businessStatus: prospect.openNow ? 'Open' : 'Closed',
                    website: prospect.website,
                    phone: prospect.phone
                  });
                  const advertiserInfo = currentAdvertisers[businessHash];
                  if (advertiserInfo) {
                    return (
                      <div style={styles.currentAdvertiserContainer}>
                        <div style={styles.currentAdvertiserHeader}>
                          <span style={styles.currentAdvertiserIcon}>✅</span>
                          <span style={styles.currentAdvertiserTitle}>Current Spectrum Reach Advertiser</span>
                        </div>
                        <p style={styles.currentAdvertiserInfo}>
                          Marked by <strong>{advertiserInfo.markedByName}</strong> on {advertiserDateText(advertiserInfo.markedAt)}
                        </p>
                        {isAdmin() && (
                          <button
                            style={styles.unmarkAdvertiserButton}
                            onClick={() => unmarkCurrentAdvertiser(prospect)}
                            title="Remove current advertiser status (Admin only)"
                          >
                            🗑️ Unmark
                          </button>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {renderProspectAiInsights(prospect)}
                {renderGemmetricLiteScanPrompt(prospect)}
                {renderLiteScanInsights(prospect)}

                <div style={styles.quickActions}>
                  {prospect.phone && (
                    <button
                      style={styles.quickActionButton}
                      onClick={() => window.open(`tel:${prospect.phone}`, '_self')}
                    >
                      📞 Call
                    </button>
                  )}
                  <button
                    style={styles.quickActionButton}
                    onClick={() => {
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(prospect.address)}`;
                      window.open(url, '_blank');
                    }}
                  >
                    🗺️ Directions
                  </button>
                  {prospect.website && (
                    <button
                      style={styles.quickActionButton}
                      onClick={() => window.open(prospect.website, '_blank')}
                    >
                      🌐 Website
                    </button>
                  )}
                  <button
                    style={styles.appointmentStrategyButton}
                    onClick={() => generateAppointmentStrategy(prospect)}
                    title="Get AI-powered appointment strategy"
                  >
                    🎯 Strategy
                  </button>
                </div>

                <div style={styles.prospectActions}>
                  <button
                    style={{ ...styles.actionButton, backgroundColor: '#10b981' }}
                    disabled
                  >
                    ✅ Saved for Later
                  </button>
                  <button
                    style={{
                      ...styles.actionButton,
                      ...(inCurrentWeekSheet ? styles.actionButtonDisabled : {})
                    }}
                    onClick={() => void addProspectCardToTodaySheet(prospect, true)}
                    disabled={inCurrentWeekSheet || !!addingToWeekByProspectId[stableProspectId]}
                  >
                    {addingToWeekByProspectId[stableProspectId]
                      ? 'Adding...'
                      : inCurrentWeekSheet
                        ? 'Added to This Week'
                        : '📋 Add to This Week'}
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}

        {activeTab === 'log' && (
          <div style={styles.logList}>
            <div style={styles.sectionHeader}>
              <h3>Call History ({log.length})</h3>
              <p>Your logged calls and interactions with prospects</p>
              <div style={styles.logActions}>
                <button
                  style={styles.logActionButton}
                  onClick={exportCallLogToCSV}
                  disabled={log.length === 0}
                  title="Export call history to CSV"
                >
                  📊 Export to CSV
                </button>
                <button
                  style={styles.logActionButton}
                  onClick={clearCallLog}
                  disabled={log.length === 0}
                  title="Clear all call history"
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>
            {log.length === 0 ? (
              <p style={styles.emptyMessage}>No call history yet. Log your first call to see it here.</p>
            ) : (
              log.map(entry => (
                <div key={entry.id} style={styles.logEntry}>
                  <div style={styles.logHeader}>
                    <h3 style={styles.logName}>{entry.name}</h3>
                    <div style={styles.logHeaderRight}>
                      <span style={styles.logOutcome}>{entry.outcome}</span>
                      <button
                        style={styles.deleteLogButton}
                        onClick={() => deleteCallLogEntry(entry.id)}
                        title="Delete this log entry"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.logBusinessInfo}>
                    <p style={styles.logCategory}>📋 {entry.category}</p>
                    <p style={styles.logAddress}>📍 {entry.address}</p>
                    {entry.phone && <p style={styles.logPhone}>📞 {entry.phone}</p>}
                    {entry.website && <p style={styles.logWebsite}>🌐 {entry.website}</p>}
                  </div>
                  
                  <div style={styles.logQuickActions}>
                    {entry.phone && (
                      <button
                        style={styles.logQuickActionButton}
                        onClick={() => window.open(`tel:${entry.phone}`, '_self')}
                        title="Call this business"
                      >
                        📞 Call
                      </button>
                    )}
                    <button
                      style={styles.logQuickActionButton}
                      onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(entry.address)}`, '_blank')}
                      title="Get directions to this business"
                    >
                      🗺️ Directions
                    </button>
                    {entry.website && (
                      <button
                        style={styles.logQuickActionButton}
                        onClick={() => window.open(entry.website, '_blank')}
                        title="Visit this business website"
                      >
                        🌐 Website
                      </button>
                    )}
                  </div>
                  
                  <p style={styles.logTime}>
                    📅 {formatMdy(new Date(entry.timestamp))} at {new Date(entry.timestamp).toLocaleTimeString()}
                  </p>
                  {entry.appointmentAt && (
                    <div style={{ fontSize: 13, color: '#111827', marginTop: 6 }}>
                      <div>🗓️ Appointment: {formatMdyTime(new Date(entry.appointmentAt))}</div>
                      <div>
                        {(entry.appointmentType || 'appointment').toString().replace('_', ' ')} • {entry.durationMinutes || 30} min
                        {entry.appointmentContactName ? ` • ${entry.appointmentContactName}` : ''}
                      </div>
                      {entry.appointmentContactPhone ? <div>☎ {entry.appointmentContactPhone}</div> : null}
                      {entry.appointmentContactEmail ? <div>✉ {entry.appointmentContactEmail}</div> : null}
                      {entry.appointmentLocation ? <div>📍 {entry.appointmentLocation}</div> : null}
                    </div>
                  )}
                  {entry.note && <p style={styles.logNote}>📝 {entry.note}</p>}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'all-prospects' && isAdmin() && (
            <div style={styles.prospectsList}>
              <div style={styles.sectionHeader}>
                <h3>All Prospects ({allProspects.length})</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <p>Showing all prospects returned from the scan</p>
                  <button
                    style={{ ...styles.adminButton, padding: '6px 10px' }}
                    onClick={exportAllProspectsToCSV}
                    disabled={allProspects.length === 0}
                    title="Export Name, Category, Address, Rating, Score in on-screen order"
                  >
                    📊 Export All ({allProspects.length})
                  </button>
                </div>
              </div>
            {allProspects.map(prospect => (
              <div key={prospect.id} style={styles.prospectCard}>
                <div style={styles.prospectHeader}>
                  <div style={styles.prospectTitleRow}>
                    <h3 style={styles.prospectName}>{prospect.name}</h3>
                    <div style={styles.headerRight}>
                      {isProspectInCurrentWeekSheet(prospect) ? (
                        <span style={styles.callSheetBadge}>Added this week</span>
                      ) : null}
                      <span style={styles.prospectScore} title={ORGANIZATION_SCORE_LABELS[selectedOrg]}>
                        {formatProspectScore(prospect)}
                      </span>
                      <button
                        style={styles.blockButton}
                        onClick={() => addToBlocklist(prospect.name, prospect.id)}
                      >
                        Block
                      </button>
                    </div>
                  </div>
                </div>
                <p style={styles.prospectCategory}>{prospect.category}</p>
                <p style={styles.prospectAddress}>{prospect.address}</p>
                
                {renderProspectAiInsights(prospect)}
                {renderGemmetricLiteScanPrompt(prospect)}
                {renderLiteScanInsights(prospect)}
                
                {/* Quick Action Buttons - moved below address */}
                <div style={styles.quickActions}>
                  {prospect.phone && (
                    <button
                      style={styles.quickActionButton}
                      onClick={() => window.open(`tel:${prospect.phone}`, '_self')}
                    >
                      📞 Call
                    </button>
                  )}
                  <button
                    style={styles.quickActionButton}
                    onClick={() => {
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(prospect.address)}`;
                      window.open(url, '_blank');
                    }}
                  >
                    🗺️ Directions
                  </button>
                  {prospect.website && (
                    <button
                      style={styles.quickActionButton}
                      onClick={() => window.open(prospect.website, '_blank')}
                    >
                      🌐 Website
                    </button>
                  )}
                  <button
                    style={styles.appointmentStrategyButton}
                    onClick={() => generateAppointmentStrategy(prospect)}
                    title="Get AI-powered appointment strategy"
                  >
                    🎯 Strategy
                  </button>
                </div>
                
                <div style={styles.prospectActions}>
                  <button
                    style={styles.actionButton}
                    onClick={() => void logCall(prospect, "Saved for Later", "")}
                  >
                    💾 Save for Later
                  </button>
                  <button
                    style={{
                      ...styles.actionButton,
                      ...(isProspectInCurrentWeekSheet(prospect) ? styles.actionButtonDisabled : {})
                    }}
                    onClick={() => void addProspectCardToTodaySheet(prospect, false)}
                    disabled={isProspectInCurrentWeekSheet(prospect) || !!addingToWeekByProspectId[getProspectStableId(prospect).id]}
                  >
                    {addingToWeekByProspectId[getProspectStableId(prospect).id]
                      ? 'Adding...'
                      : isProspectInCurrentWeekSheet(prospect)
                        ? 'Added to This Week'
                        : '📋 Add to This Week'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'blocked' && isAdmin() && (
          <div style={styles.blockedPanel}>
            <div style={styles.sectionHeader}>
              <h3>Blocked Businesses ({blocklist.length})</h3>
              <p>These businesses will be filtered out from future scans</p>
            </div>
            <div style={styles.blocklistContainer}>
              {blocklist.map((stableId) => (
                <div key={stableId} style={styles.blocklistItem}>
                  <span style={styles.blocklistName}>{resolveBlockedDisplayName(stableId)}</span>
                  <button
                    style={styles.removeBlocklistButton}
                    onClick={() => removeFromBlocklist(stableId)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {blocklist.length === 0 && (
                <p style={styles.emptyMessage}>No blocked businesses</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'current-advertisers' && isAdmin() && (
          <div style={styles.currentAdvertisersPanel}>
            <div style={styles.sectionHeader}>
              <h3>Current Advertisers ({Object.keys(currentAdvertisers).length})</h3>
              <p>Businesses marked as current Spectrum Reach advertisers</p>
            </div>
            <div style={styles.currentAdvertisersContainer}>
              {Object.entries(currentAdvertisers).map(([businessHash, advertiserInfo]) => (
                <div key={businessHash} style={styles.currentAdvertiserItem}>
                  <div style={styles.currentAdvertiserItemInfo}>
                    <span style={styles.currentAdvertiserName}>{advertiserInfo.businessName}</span>
                    <span style={styles.currentAdvertiserDetails}>
                      Marked by <strong>{advertiserInfo.markedByName}</strong> on{' '}
                      {advertiserDateText(advertiserInfo.markedAt)}
                    </span>
                  </div>
                  <button
                    style={styles.removeCurrentAdvertiserButton}
                    onClick={async () => {
                      try {
                        await removeCurrentAdvertiser(businessHash, selectedOrg);
                        // The unmarkCurrentAdvertiser function will update the local state
                        // But we need to manually update it here since we don't have the prospect object
                        setCurrentAdvertisers(prev => {
                          const newState = { ...prev };
                          delete newState[businessHash];
                          return newState;
                        });
                        alert('✅ Business removed from current advertisers');
                      } catch (error: any) {
                        console.error('Error removing current advertiser:', error);
                        alert('❌ Error: ' + error.message);
                      }
                    }}
                  >
                    🗑️ Remove
                  </button>
                </div>
              ))}
              {Object.keys(currentAdvertisers).length === 0 && (
                <p style={styles.emptyMessage}>No current advertisers marked</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'admin' && isAdmin() && (
          <div style={styles.adminPanel}>
            {/* Cache Management - All admins can see stats, only SuperAdmins can clear */}
            <div style={styles.adminSection}>
              <h3 style={styles.adminSectionTitle}>Cache Management</h3>
              <div style={styles.cacheStats}>
                <p style={styles.cacheStatsText}>
                  📊 <strong>AI Cache Statistics:</strong>
                </p>
                <p style={styles.cacheStatsDetail}>
                  Total cached businesses: <strong>{cacheStats.total}</strong>
                </p>
                <p style={styles.cacheStatsDetail}>
                  Valid entries: <strong style={{color: '#28a745'}}>{cacheStats.valid}</strong> | 
                  Expired entries: <strong style={{color: '#dc3545'}}>{cacheStats.expired}</strong>
                </p>
              </div>
              {isSuperAdmin() && (
                <button 
                  style={styles.adminButton}
                  onClick={clearAICache}
                >
                  🗑️ Clear AI Cache
                </button>
              )}
              <button 
                style={styles.adminButton}
                onClick={() => setCacheStats(getCacheStats())}
              >
                🔄 Refresh Cache Stats
              </button>
              <p style={styles.adminNote}>
                {isSuperAdmin() 
                  ? 'Clears cached prospect analyses to force re-analysis' 
                  : 'Cache statistics are read-only for regular admins'
                }
              </p>
            </div>

            {/* SuperAdmin Only Sections */}
            {isSuperAdmin() && (
              <>
                {/* Universal Blocklist Management */}
                <div style={styles.adminSection}>
                  <h3 style={styles.adminSectionTitle}>Universal Blocklist Management</h3>
                  <p style={styles.adminNote}>
                    Blocked businesses are shared across all users. Changes affect everyone.
                  </p>
                  <button 
                    style={styles.adminButton}
                    onClick={async () => {
                      try {
                        const universalBlocklist = await getUniversalBlocklist();
                        alert(`Universal blocklist: ${universalBlocklist.length} items\n${universalBlocklist.join(', ')}`);
                      } catch (error) {
                        console.error('Error getting universal blocklist:', error);
                        alert('Error loading universal blocklist');
                      }
                    }}
                  >
                    🔍 View Universal Blocklist
                  </button>
                  <button 
                    style={styles.adminButton}
                    onClick={testBlocklistPersistence}
                  >
                    🔍 Test Blocklist Persistence
                  </button>
                  <p style={styles.adminNote}>Current universal blocklist: {blocklist.length} items</p>
                </div>

                {/* User Data Testing */}
                <div style={styles.adminSection}>
                  <h3 style={styles.adminSectionTitle}>User Data Testing</h3>
                  <button 
                    style={styles.adminButton}
                    onClick={testDataPersistence}
                  >
                    🧪 Test User Data Persistence
                  </button>
                  <p style={styles.adminNote}>Adds test data to call logs and saved prospects</p>
                  <p style={styles.adminNote}>Current user: {currentUser?.id} | Call logs: {log.length} | Saved: {savedProspects.length}</p>
                </div>

                {/* Universal Blocklist Debug */}
                <div style={styles.adminSection}>
                  <h3 style={styles.adminSectionTitle}>Universal Blocklist Debug</h3>
                  <button 
                    style={styles.adminButton}
                    onClick={checkBlocklistStatus}
                  >
                    🔍 Check Blocklist Status
                  </button>
                  <button 
                    style={styles.adminButton}
                    onClick={testBlocklistPersistence}
                  >
                    🧪 Test Blocklist Persistence
                  </button>
                  <button 
                    style={styles.adminButton}
                    onClick={addTestToBlocklist}
                  >
                    ➕ Add Test to Blocklist
                  </button>
                  <button 
                    style={styles.adminButton}
                    onClick={clearAllBlocklist}
                    disabled={blocklist.length === 0}
                  >
                    🗑️ Clear All Blocklist ({blocklist.length} items)
                  </button>
                  <p style={styles.adminNote}>Current universal blocklist: {blocklist.length} items</p>
                  <p style={styles.adminNote}>Blocklist loaded: {blocklistLoaded ? 'Yes' : 'No'}</p>
                </div>
              </>
            )}

            {/* Export Prospects - SuperAdmin Only */}
            {isSuperAdmin() && (
              <div style={styles.adminSection}>
                <h3 style={styles.adminSectionTitle}>Export Prospects</h3>
                <button 
                  style={styles.adminButton}
                  onClick={exportProspectsToCSV}
                  disabled={scanResults.length === 0}
                >
                  📊 Export Current Scan ({scanResults.length} prospects)
                </button>
                <button 
                  style={styles.adminButton}
                  onClick={exportAllProspectsToCSV}
                  disabled={allProspects.length === 0}
                >
                  📊 Export All Prospects ({allProspects.length} prospects)
                </button>
              </div>
            )}

            {/* User Management (Admin and SuperAdmin) */}
            {isAdmin() && (
              <div style={styles.adminSection}>
                <h3 style={styles.adminSectionTitle}>
                  User Management {!isSuperAdmin() && '(Cannot manage SuperAdmins)'}
                </h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button style={styles.adminButton} onClick={refreshUsers}>Refresh</button>
                </div>
                <div style={styles.userList}>
                  {allUsers.map(user => (
                    <div key={user.id} style={styles.userItem}>
                      <div style={styles.adminUserInfo}>
                        <span style={styles.adminUserName}>{user.name}</span>
                        <span style={styles.userEmail}>{user.email}</span>
                        <span style={styles.adminUserRole}>{user.role}</span>
                      </div>
                      <div style={styles.userActions}>
                        {/* Regular admins can only manage non-SuperAdmin users */}
                        {(!isSuperAdmin() && user.role === 'SuperAdmin') ? (
                          <span style={styles.readOnlyRole}>SuperAdmin (Read Only)</span>
                        ) : (
                          <select
                            style={styles.roleSelect}
                            value={user.role}
                            onChange={(e) => updateUserRole(user.id, e.target.value as 'user' | 'admin' | 'SuperAdmin')}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            {isSuperAdmin() && <option value="SuperAdmin">SuperAdmin</option>}
                          </select>
                        )}
                        {/* Only allow deletion of non-SuperAdmin users (unless you're SuperAdmin) */}
                        {user.id !== currentUser?.id && (isSuperAdmin() || user.role !== 'SuperAdmin') && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              style={styles.deleteUserButton}
                              onClick={() => deleteUser(user.id)}
                            >
                              Delete (profile)
                            </button>
                            {isSuperAdmin() && (
                              <>
                                <button
                                  style={styles.deleteUserButton}
                                  onClick={async () => {
                                    try {
                                      await adminUpdateAuthUser(user.id, 'suspend');
                                      alert('User suspended');
                                    } catch (e: any) {
                                      alert('Failed to suspend: ' + (e?.message || e));
                                    }
                                  }}
                                >
                                  Suspend
                                </button>
                                <button
                                  style={styles.deleteUserButton}
                                  onClick={async () => {
                                    try {
                                      await adminUpdateAuthUser(user.id, 'unsuspend');
                                      alert('User re-enabled');
                                    } catch (e: any) {
                                      alert('Failed to re-enable: ' + (e?.message || e));
                                    }
                                  }}
                                >
                                  Unsuspend
                                </button>
                                <button
                                  style={styles.deleteUserButton}
                                  onClick={async () => {
                                    // Show a double-confirm dialog with target info and request current admin password
                                    const target = allUsers.find(u => u.id === user.id);
                                    const confirmed = window.confirm(`Hard delete this user?\n\nName: ${target?.name || ''}\nEmail: ${target?.email || ''}\n\nThis cannot be undone.`);
                                    if (!confirmed) return;
                                    const pwd = window.prompt('Enter your admin password to confirm:');
                                    if (!pwd) return;
                                    try {
                                      await reauthenticateCurrentUser(pwd);
                                      await adminUpdateAuthUser(user.id, 'hardDelete');
                                      // Also remove from local state
                                      const updatedUsers = allUsers.filter(u => u.id !== user.id);
                                      setAllUsers(updatedUsers);
                                      alert('User hard deleted');
                                    } catch (e: any) {
                                      alert('Failed to hard delete: ' + (e?.message || e));
                                    }
                                  }}
                                >
                                  Hard Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Algorithm Settings - SuperAdmin Only */}
            {isSuperAdmin() && (
              <div style={styles.adminSection}>
                <h3 style={styles.adminSectionTitle}>Algorithm Settings</h3>
                <div style={styles.algorithmSettings}>
                  <label style={styles.settingLabel}>
                    <input
                      type="checkbox"
                      checked={showAllProspects}
                      onChange={(e) => setShowAllProspects(e.target.checked)}
                      style={styles.settingCheckbox}
                    />
                    Show all prospects (not just limited list)
                  </label>
                </div>
              </div>
            )}

            {/* Invitation Management - Available to all admins */}
            <div style={styles.adminSection}>
              <h3 style={styles.adminSectionTitle}>Invitation Management</h3>
              <p style={styles.adminNote}>
                Generate invitation codes for new users. Only invited users can register.
              </p>
              <button 
                style={styles.adminButton}
                onClick={handleGenerateInvitation}
              >
                📧 Generate Invitation Code
              </button>
              <button 
                style={styles.adminButton}
                onClick={async () => {
                  try {
                    const invitations = await getInvitations();
                    if (invitations.length === 0) {
                      alert('No invitations found');
                      return;
                    }
                    
                    const invitationList = invitations.map((inv: any) => 
                      `Code: ${inv.code}\nInvited by: ${inv.invitedBy}\nCreated: ${formatMdy(inv.createdAt.toDate())}\nUsed: ${inv.used ? 'Yes' : 'No'}\nExpires: ${formatMdy(inv.expiresAt.toDate())}`
                    ).join('\n\n');
                    
                    alert(`Invitations:\n\n${invitationList}`);
                  } catch (error: any) {
                    alert('❌ Error loading invitations: ' + error.message);
                  }
                }}
              >
                📋 View All Invitations
              </button>
              <p style={styles.adminNote}>Invitations expire after 7 days</p>
            </div>
          </div>
        )}
      </div>

      {/* Appointment Strategy Modal */}
      {showAppointmentModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {isGemmetricOrg ? '🎯 AI Visibility Strategy' : '🎯 Appointment Strategy'}
              </h2>
              <button
                style={styles.closeModalButton}
                onClick={() => {
                  setShowAppointmentModal(false);
                  setAppointmentStrategy(null);
                }}
              >
                ✕
              </button>
            </div>
            
            {isGeneratingStrategy ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}>🤖</div>
                <p style={styles.loadingText}>Generating AI-powered strategy...</p>
              </div>
            ) : appointmentStrategy ? (
              <div style={styles.strategyContent}>
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>📞 Opening Line</h3>
                  <p style={styles.strategyText}>{appointmentStrategy.openingLine}</p>
                </div>
                
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>⏰ Best Time to Call</h3>
                  <p style={styles.strategyText}>{appointmentStrategy.bestTimeToCall}</p>
                </div>
                
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>💡 Key Talking Points</h3>
                  <ul style={styles.strategyList}>
                    {appointmentStrategy.keyTalkingPoints.map((point, index) => (
                      <li key={index} style={styles.strategyListItem}>{strategyText(point)}</li>
                    ))}
                  </ul>
                </div>
                
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>🛡️ Objection Handlers</h3>
                  <ul style={styles.strategyList}>
                    {appointmentStrategy.objectionHandlers.map((handler, index) => (
                      <li key={index} style={styles.strategyListItem}>{strategyText(handler)}</li>
                    ))}
                  </ul>
                </div>
                
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>🎯 Value Proposition</h3>
                  <p style={styles.strategyText}>{appointmentStrategy.valueProposition}</p>
                </div>
                
                <div style={styles.strategySection}>
                  <h3 style={styles.strategySectionTitle}>✅ Call to Action</h3>
                  <p style={styles.strategyText}>{appointmentStrategy.callToAction}</p>
                </div>

                {/* New enriched sections - render only if present */}
                {appointmentStrategy.hookLines && appointmentStrategy.hookLines.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🎣 Hook Lines</h3>
                    <div style={styles.chipsRow}>
                      {appointmentStrategy.hookLines.map((h, i) => (
                        <span key={i} style={styles.chip} onClick={() => navigator.clipboard.writeText(strategyText(h))} title="Click to copy">{strategyText(h)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {appointmentStrategy.competitiveAngles && appointmentStrategy.competitiveAngles.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🏁 Competitive Angles</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.competitiveAngles.map((p, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(p)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {appointmentStrategy.verticalInsights && appointmentStrategy.verticalInsights.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>📚 Vertical Insights</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.verticalInsights.map((p, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(p)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {appointmentStrategy.discoveryQuestions && appointmentStrategy.discoveryQuestions.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🧭 Discovery Questions</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.discoveryQuestions.map((p, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(p)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {isGemmetricOrg && appointmentStrategy.visibilityPriorities && appointmentStrategy.visibilityPriorities.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🔎 AI Visibility Priorities</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.visibilityPriorities.map((item, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {isGemmetricOrg && appointmentStrategy.liteScanInsights && appointmentStrategy.liteScanInsights.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🧪 Initial Assessment Insights</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.liteScanInsights.map((item, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!isGemmetricOrg && appointmentStrategy.mediaMix && (appointmentStrategy.mediaMix as any[]).length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>📺 Recommended Media Mix (Spectrum Reach)</h3>
                    <div style={styles.mediaMixTable}>
                      <div style={styles.mediaMixHeaderRow}>
                        <div style={styles.mediaMixHeader}>Channel</div>
                        <div style={styles.mediaMixHeader}>% Mix</div>
                        <div style={styles.mediaMixHeader}>Why</div>
                      </div>
                      {(appointmentStrategy.mediaMix as any[]).map((row: any, i: number) => {
                        const channel = (row && typeof row === 'object')
                          ? strategyText(row.channel || row.name || row)
                          : strategyText(row);
                        const percent = (row && typeof row === 'object' && row.percent !== undefined)
                          ? `${strategyText(row.percent)}%`
                          : '';
                        const why = (row && typeof row === 'object')
                          ? strategyText(row.why || row.reason || '')
                          : '';
                        return (
                          <div key={i} style={styles.mediaMixRow}>
                            <div style={styles.mediaMixCell}>{channel}</div>
                            <div style={styles.mediaMixCell}>{percent}</div>
                            <div style={styles.mediaMixCell}>{why}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {appointmentStrategy.plan30Day && appointmentStrategy.plan30Day.length > 0 && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>🗓️ 30‑Day Plan</h3>
                    <ul style={styles.strategyList}>
                      {appointmentStrategy.plan30Day.map((p, i) => (
                        <li key={i} style={styles.strategyListItem}>{strategyText(p)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {(appointmentStrategy.introEmail && appointmentStrategy.introEmail.trim()) && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>✉️ Intro Email</h3>
                    <pre style={styles.preBlock} onClick={() => navigator.clipboard.writeText(appointmentStrategy.introEmail || '')} title="Click to copy">
{appointmentStrategy.introEmail}
                    </pre>
                  </div>
                )}

                {(appointmentStrategy.followUpEmail && appointmentStrategy.followUpEmail.trim()) && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>✉️ Follow-up Email</h3>
                    <pre style={styles.preBlock} onClick={() => navigator.clipboard.writeText(appointmentStrategy.followUpEmail || '')} title="Click to copy">
{appointmentStrategy.followUpEmail}
                    </pre>
                  </div>
                )}

                {(appointmentStrategy.voicemailScript && appointmentStrategy.voicemailScript.trim()) && (
                  <div style={styles.strategySection}>
                    <h3 style={styles.strategySectionTitle}>📞 Voicemail Script</h3>
                    <pre style={styles.preBlock} onClick={() => navigator.clipboard.writeText(appointmentStrategy.voicemailScript || '')} title="Click to copy">
{appointmentStrategy.voicemailScript}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.errorContainer}>
                <p style={styles.errorText}>Failed to generate strategy. Please try again.</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* PWA Install Prompt */}
      <InstallPrompt currentUserId={currentUser?.id} />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    color: 'white',
    padding: '20px 24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    position: 'relative' as const,
    backgroundSize: '200% 200%',
    animation: 'headerGradient 15s ease infinite',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
  },
  brandSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap' as const,
    minWidth: '0',
    flex: '1',
  },
  brandSubtitle: {
    margin: 0,
    fontSize: '16px',
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
    flexWrap: 'wrap' as const,
    minWidth: '0',
    flexShrink: 0,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '4px',
  },
  userName: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  userRole: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
    textTransform: 'capitalize' as const,
    backgroundColor: 'rgba(255,255,255,0.15)',
    padding: '2px 8px',
    borderRadius: '8px',
    fontWeight: '500',
  },
  logoutButton: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(5px)',
  },
  controls: {
    padding: '16px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  locationControls: {
    marginBottom: '16px',
  },
  locationButtons: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  locationButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    flex: 1,
  },
  addressInput: {
    marginBottom: '12px',
  },
  addressInputField: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
  },
  currentLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  locationLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  locationText: {
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  controlRow: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
  },
  slider: {
    width: '100%',
  },
  scanButton: {
    width: '100%',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  mapContainer: {
    height: '500px',
    borderBottom: '1px solid #e5e7eb',
  },
  tabContainer: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  tabRow: {
    display: 'flex',
    borderBottom: '1px solid #f3f4f6',
  },
  tabButton: {
    flex: '1',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '16px 12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#6b7280',
    transition: 'all 0.2s ease',
    borderBottom: '3px solid transparent',
  },
  activeTabButton: {
    color: '#3b82f6',
    borderBottom: '3px solid #3b82f6',
    backgroundColor: '#f8fafc',
  },
  content: {
    flex: 1,
    overflowY: 'auto' as const,
    backgroundColor: '#f5f5f5',
  },
  prospectsList: {
    padding: '16px',
    paddingTop: '24px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  prospectCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  prospectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  prospectTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    gap: '12px',
  },
  prospectName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '8px',
    flexShrink: 0,
  },
  prospectScore: {
    backgroundColor: '#10b981',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  callSheetBadge: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 8px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap' as const,
  },
  prospectCategory: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#6b7280',
  },
  prospectAddress: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#374151',
  },
  prospectActions: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px',
  },
  outcomeSelect: {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
  },
  noteInput: {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    flex: 1,
  },
  actionButtonDisabled: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    cursor: 'default',
  },
  blockButton: {
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '60px',
    transition: 'background-color 0.2s ease',
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '8px',
  },
  quickActionButton: {
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    flex: 1,
  },
  removeButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '8px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '8px',
  },
  logList: {
    padding: '16px',
  },
  logEntry: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  logHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
  },
  logOutcome: {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  logTime: {
    margin: '4px 0',
    fontSize: '12px',
    color: '#6b7280',
  },
  logNote: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#374151',
  },
  deleteLogButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    borderRadius: '4px',
  },
  logBusinessInfo: {
    margin: '12px 0',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  logCategory: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500',
  },
  logAddress: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#374151',
  },
  logPhone: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#374151',
  },
  logWebsite: {
    margin: '4px 0',
    fontSize: '14px',
    color: '#3b82f6',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  logQuickActions: {
    display: 'flex',
    gap: '8px',
    margin: '12px 0',
    flexWrap: 'wrap' as const,
  },
  logQuickActionButton: {
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    flex: '1 1 auto',
    minWidth: '80px',
  },
  logActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
  },
  logActionButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  // Admin styles
  adminPanel: {
    padding: '16px',
  },
  adminSection: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  adminSectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
  },
  adminButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },
  blocklistContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  blocklistItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  blocklistName: {
    fontSize: '14px',
    color: '#374151',
  },
  removeBlocklistButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  emptyMessage: {
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic',
    textAlign: 'center' as const,
    margin: '16px 0',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  userItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },

  userEmail: {
    fontSize: '12px',
    color: '#6b7280',
  },
  adminUserInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    flex: 1,
  },
  adminUserName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
  },
  adminUserRole: {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'capitalize' as const,
    backgroundColor: '#e5e7eb',
    padding: '2px 8px',
    borderRadius: '8px',
  },
  cacheStats: {
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '12px',
    border: '1px solid #e9ecef',
  },
  cacheStatsText: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#495057',
    fontWeight: '500',
  },
  cacheStatsDetail: {
    margin: '4px 0',
    fontSize: '13px',
    color: '#6c757d',
  },
  userActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  roleSelect: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
  },
  readOnlyRole: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    fontSize: '12px',
    fontStyle: 'italic',
    border: '1px solid #d1d5db',
  },
  deleteUserButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  algorithmSettings: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  settingLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  settingCheckbox: {
    width: '16px',
    height: '16px',
  },
  deleteButton: {
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    padding: '8px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    flex: 1,
  },
  sectionHeader: {
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  blockedPanel: {
    padding: '16px',
  },
  adminNote: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '8px',
    fontStyle: 'italic',
  },
  instructionsOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  instructionsWindow: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
  instructionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e5e7eb',
  },
  instructionsTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
  },
  closeInstructionsButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  instructionsContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  instructionStep: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  stepNumber: {
    backgroundColor: '#3b82f6',
    color: 'white',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600',
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
  },
  stepDescription: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#6b7280',
  },
  instructionNote: {
    backgroundColor: '#f3f4f6',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginTop: '8px',
  },
  noteText: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#374151',
  },
  dontShowAgainContainer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  dontShowAgainLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#6b7280',
  },
  dontShowAgainCheckbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  dontShowAgainText: {
    userSelect: 'none' as const,
  },
  instructionsFooter: {
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'center',
  },
  getStartedButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  // AI Analysis Styles
  aiAnalysisContainer: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    borderLeft: '4px solid #3b82f6',
  },
  aiAnalysisHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e2e8f0',
  },
  aiAnalysisIcon: {
    fontSize: '16px',
  },
  aiAnalysisTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
    flex: 1,
  },
  aiConfidence: {
    fontSize: '12px',
    color: '#64748b',
    backgroundColor: '#e2e8f0',
    padding: '2px 8px',
    borderRadius: '12px',
  },
  aiReasoning: {
    marginBottom: '12px',
  },
  aiReasoningStrong: {
    fontSize: '13px',
    color: '#1e293b',
    display: 'block',
    marginBottom: '4px',
  },
  aiReasoningParagraph: {
    margin: 0,
    fontSize: '13px',
    lineHeight: '1.4',
    color: '#475569',
  },
  aiRecommendations: {
    marginTop: '8px',
  },
  aiRecommendationsStrong: {
    fontSize: '13px',
    color: '#1e293b',
    display: 'block',
    marginBottom: '6px',
  },
  recommendationsList: {
    margin: 0,
    paddingLeft: '16px',
  },
  recommendationItem: {
    fontSize: '12px',
    color: '#475569',
    marginBottom: '2px',
    lineHeight: '1.3',
  },
  liteScanContainer: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    borderLeft: '4px solid #16a34a',
  },
  liteScanHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap' as const,
  },
  liteScanIcon: {
    fontSize: '16px',
  },
  liteScanTitleBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    gap: '2px',
  },
  liteScanTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#14532d',
  },
  liteScanSubtitle: {
    fontSize: '11px',
    color: '#166534',
    opacity: 0.85,
  },
  liteScanScoreBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '2px',
  },
  liteScanScore: {
    fontSize: '12px',
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '2px 8px',
    borderRadius: '12px',
    fontWeight: '600',
  },
  liteScanScoreTier: {
    fontSize: '11px',
    color: '#14532d',
    fontWeight: '500',
  },
  liteScanOpportunityBadge: {
    fontSize: '12px',
    color: '#92400e',
    backgroundColor: '#fef3c7',
    padding: '2px 8px',
    borderRadius: '12px',
    fontWeight: '500',
  },
  liteScanSummary: {
    margin: '0 0 6px 0',
    fontSize: '13px',
    color: '#166534',
    fontWeight: '500',
  },
  liteScanScoreDescription: {
    margin: '0 0 10px 0',
    fontSize: '12px',
    color: '#166534',
  },
  liteScanProspectingSection: {
    marginBottom: '12px',
    padding: '10px 12px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '8px',
  },
  liteScanProspectingHeading: {
    display: 'block',
    fontSize: '12px',
    color: '#92400e',
    marginBottom: '6px',
  },
  liteScanProspectingList: {
    margin: 0,
    paddingLeft: '18px',
  },
  liteScanProspectingItem: {
    fontSize: '12px',
    color: '#78350f',
    lineHeight: 1.45,
    marginBottom: '4px',
  },
  liteScanSnapshotSection: {
    display: 'grid',
    gap: '10px',
    marginBottom: '12px',
  },
  liteScanSnapshotGroup: {
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    padding: '10px 12px',
    border: '1px solid #dcfce7',
  },
  liteScanSnapshotHeading: {
    display: 'block',
    fontSize: '12px',
    color: '#14532d',
    marginBottom: '6px',
  },
  liteScanSnapshotList: {
    margin: 0,
    paddingLeft: '0',
    listStyle: 'none',
  },
  liteScanSnapshotStrength: {
    fontSize: '12px',
    color: '#14532d',
    marginBottom: '4px',
    lineHeight: '1.4',
  },
  liteScanSnapshotOpportunity: {
    fontSize: '12px',
    color: '#92400e',
    marginBottom: '4px',
    lineHeight: '1.4',
  },
  liteScanTechnicalSection: {
    marginTop: '4px',
    paddingTop: '10px',
    borderTop: '1px solid #bbf7d0',
  },
  liteScanTechnicalHeading: {
    display: 'block',
    fontSize: '12px',
    color: '#14532d',
    marginBottom: '8px',
  },
  liteScanFindingsList: {
    margin: 0,
    paddingLeft: 0,
    listStyle: 'none',
  },
  liteScanFindingItem: {
    fontSize: '12px',
    color: '#14532d',
    marginBottom: '8px',
    padding: '8px 10px',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #ecfdf5',
  },
  liteScanFindingTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
  },
  liteScanFindingConfidence: {
    fontSize: '10px',
    color: '#6b7280',
    fontWeight: 500,
    fontStyle: 'italic' as const,
  },
  liteScanFindingWhy: {
    margin: '0 0 4px 0',
    fontSize: '12px',
    color: '#166534',
    lineHeight: '1.4',
  },
  liteScanFindingDetail: {
    margin: 0,
    fontSize: '11px',
    color: '#4b5563',
    fontStyle: 'italic',
  },
  liteScanPrompt: {
    marginTop: '10px',
    padding: '10px 12px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '8px',
  },
  liteScanPromptText: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    color: '#0369a1',
    lineHeight: 1.4,
  },
  liteScanCtaButton: {
    width: '100%',
    backgroundColor: '#0284c7',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  liteScanRerunButton: {
    marginTop: '10px',
    backgroundColor: 'transparent',
    border: '1px solid #bae6fd',
    color: '#0369a1',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },
  // Appointment Strategy Button
  appointmentStrategyButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
  },
  closeModalButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '40px 20px',
  },
  loadingSpinner: {
    fontSize: '48px',
    marginBottom: '16px',
    animation: 'pulse 2s infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  strategyContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  strategySection: {
    borderBottom: '1px solid #f3f4f6',
    paddingBottom: '16px',
  },
  strategySectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 8px 0',
  },
  strategyText: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#374151',
    margin: 0,
  },
  chipsRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  chip: {
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    padding: '6px 10px',
    borderRadius: '9999px',
    border: '1px solid #c7d2fe',
    cursor: 'pointer',
    fontSize: '12px',
  },
  mediaMixTable: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  mediaMixHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 3fr',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  mediaMixHeader: {
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: '13px',
  },
  mediaMixRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 3fr',
    borderTop: '1px solid #f3f4f6',
  },
  mediaMixCell: {
    padding: '8px 10px',
    fontSize: '13px',
  },
  preBlock: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px',
    whiteSpace: 'pre-wrap' as const,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '12px',
    lineHeight: '1.5',
    cursor: 'copy',
  },
  strategyList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  },
  strategyListItem: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#374151',
    marginBottom: '4px',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  errorText: {
    fontSize: '16px',
    color: '#dc2626',
    margin: 0,
  },
  // Load More Styles
  loadMoreContainer: {
    textAlign: 'center' as const,
    marginTop: '20px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    gridColumn: '1 / -1', // Span all columns in the grid
  },
  loadMoreButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':disabled': {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed',
    },
  },
  loadMoreInfo: {
    marginTop: '8px',
    fontSize: '14px',
    color: '#6c757d',
    fontStyle: 'italic',
    margin: '8px 0 0 0',
  },
  // Current Advertiser Styles
  currentAdvertiserContainer: {
    backgroundColor: '#e8f5e8',
    border: '2px solid #4caf50',
    borderRadius: '8px',
    padding: '12px',
    margin: '8px 0',
    position: 'relative' as const,
  },
  currentAdvertiserHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  currentAdvertiserIcon: {
    fontSize: '16px',
  },
  currentAdvertiserTitle: {
    fontWeight: 'bold',
    color: '#2e7d32',
    fontSize: '14px',
  },
  currentAdvertiserInfo: {
    margin: '4px 0',
    fontSize: '13px',
    color: '#555',
  },
  unmarkAdvertiserButton: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    backgroundColor: '#ff5722',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  markAdvertiserButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    fontWeight: 'bold',
  },
  currentAdvertisersPanel: {
    padding: '16px',
  },
  currentAdvertisersContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  currentAdvertiserItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  currentAdvertiserItemInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    flex: 1,
  },
  currentAdvertiserName: {
    fontSize: '14px',
    color: '#374151',
  },
  currentAdvertiserDetails: {
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  removeCurrentAdvertiserButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default App;
